// src/components/MyPage.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  Avatar, 
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Grid,
  CardMedia,
  CircularProgress,
  TextField,
  Switch,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Tooltip,
  ListItemAvatar,
  Paper,
  Slide,
  LinearProgress
} from "@mui/material";
import { auth, googleProvider, db } from "../firebase";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InstagramIcon from '@mui/icons-material/Instagram';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import { collection, query, where, onSnapshot, orderBy, doc, updateDoc, limit, startAfter, getDocs, getDoc, deleteDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { onAuthStateChanged, updateProfile, signInWithPopup, signOut } from "firebase/auth";
import GridOnIcon from '@mui/icons-material/GridOn';
import ViewListIcon from '@mui/icons-material/ViewList';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import PushPinIcon from '@mui/icons-material/PushPin';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { handleGoogleLogin } from "../services/authService";
import InAppBrowserModal from "./InAppBrowserModal";
import SnapFollowBottomSheet from './SnapFollowBottomSheet';
import { isMobileDevice } from "../utils/browserDetect";
import debounce from 'lodash/debounce';

// 스타일 상수 추가
const bottomSheetStyle = {
  position: 'fixed',
  bottom: 0,
  left: '50%',
  transform: 'translateX(-50%)',
  margin: 0,
  maxHeight: '50vh',
  borderRadius: '16px 16px 0 0',
  width: '100%',
  maxWidth: '600px',
  transition: 'transform 0.3s ease-out',
  '&.MuiDialog-root': {
    '& .MuiDialog-paper': {
      transform: 'translateY(100%)',
      transition: 'transform 0.3s ease-out',
    },
    '&.MuiDialog-open': {
      '& .MuiDialog-paper': {
        transform: 'translateY(0)',
      },
    },
  },
};

const bottomSheetPaperSx = {
  position: 'fixed',
  m: 0,
  bottom: 0,
  left: '50%',
  transform: 'translateX(-50%)',
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  maxWidth: '600px',
  width: '100%',
  boxShadow: 8,
  pb: 2,
  zIndex: 1302,
  bgcolor: '#fff',
};

const sheetButtonSx = {
  width: '100%',
  justifyContent: 'flex-start',
  px: 2,
  py: 2,
  fontSize: '1.1rem',
  fontWeight: 700,
  color: '#000',
  borderRadius: 0,
  minHeight: 48,
  borderBottom: '1px solid #eee',
  textTransform: 'none',
  fontFamily: "'NanumSquareBold', sans-serif",
  '&:last-of-type': { borderBottom: 'none' }
};

const sheetButtonDangerSx = {
  ...sheetButtonSx,
  color: '#F44336',
  borderBottom: 'none',
  fontFamily: "'NanumSquareBold', sans-serif"
};

// 칩 스타일 상수 (완독칩과 100% 동일하게)
const chipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  fontWeight: 600,
  height: '20px',
  minHeight: '20px',
  lineHeight: '20px',
  borderRadius: 99,
  background: '#333',
  color: '#fff',
  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  border: '2px solid #333',
  minWidth: 0, // hug
  width: 'auto', // hug
  textAlign: 'center',
  userSelect: 'none',
  boxSizing: 'border-box',
  verticalAlign: 'middle',
  padding: '0 8px',
  marginRight: '0.2rem',
};

function MyPage() {
  const { userId: paramUserId } = useParams();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(null);
  const [profileUser, setProfileUser] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [userExists, setUserExists] = useState(true);

  const [dataLoading, setDataLoading] = useState(true);
  const [copyMessage, setCopyMessage] = useState("");
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [userBooks, setUserBooks] = useState([]);
  const [highlightsCount, setHighlightsCount] = useState(0);
  const [booksCount, setBooksCount] = useState(0);
  const [userHighlightsByBook, setUserHighlightsByBook] = useState({});
  const [bookHighlightCounts, setBookHighlightCounts] = useState({});
  const navigate = useNavigate();
  const [newNickname, setNewNickname] = useState("");
  const [nicknameError, setNicknameError] = useState("");
  const [currentView, setCurrentView] = useState('grid');
  const [selectedHighlight, setSelectedHighlight] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isNicknameDialogOpen, setIsNicknameDialogOpen] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followingCount, setFollowingCount] = useState(0);
  const [followingList, setFollowingList] = useState([]);
  const [isFollowingListOpen, setIsFollowingListOpen] = useState(false);
  const [isFollowingListLoading, setIsFollowingListLoading] = useState(false);
  const [inAppBrowserModalOpen, setInAppBrowserModalOpen] = useState(false);
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);
  const [bio, setBio] = useState("");
  const [followerCount, setFollowerCount] = useState(0);
  const [isFollowerDialogOpen, setIsFollowerDialogOpen] = useState(false);
  const [showSnapFollowSheet, setShowSnapFollowSheet] = useState(false);

  // 게임 관련 상태 추가
  const [userTests, setUserTests] = useState([]);
  const [testLoading, setTestLoading] = useState(false);
  const [showGameBanner, setShowGameBanner] = useState(false);
  const [gameBannerDisabled, setGameBannerDisabled] = useState(false);

  // 랭킹 관련 상태 추가
  const [rankingData, setRankingData] = useState({
    topRatedBooks: [],
    mostHighlightedBooks: [],
    favoriteGenres: [],
    readingStats: {}
  });
  const [rankingLoading, setRankingLoading] = useState(false);

  // Ranking 섹션으로 스크롤하기 위한 ref
  const rankingSectionRef = useRef(null);
  // Game View 섹션으로 스크롤하기 위한 ref
  const gameSectionRef = useRef(null);

  // 랜덤 배경 이미지 선택 (성능 최적화)
  const randomBackgroundImage = useMemo(() => {
    const images = ['home-01.png', 'home-02.png', 'home-03.png', 'home-04.png', 'home-05.png', 'home-06.png', 'home-07.png', 'home-08.png'];
    return images[Math.floor(Math.random() * images.length)];
  }, []);

  // 하이라이트 개수 최적화 (성능 향상)
  const optimizedHighlightCounts = useMemo(() => {
    return bookHighlightCounts;
  }, [bookHighlightCounts]);

  // 전체 책 목록 최적화 (성능 향상)
  const optimizedUserBooks = useMemo(() => {
    return userBooks || [];
  }, [userBooks]);

  // ISBN 정규화 함수 (10자리 ↔ 13자리 변환)
  const normalizeISBN = (isbn) => {
    if (!isbn) return null;
    
    const cleanISBN = isbn.toString().replace(/[-\s]/g, '');
    
    // 10자리 ISBN을 13자리로 변환
    if (cleanISBN.length === 10) {
      const prefix = '978';
      const isbn10 = cleanISBN.substring(0, 9);
      const checkDigit = calculateCheckDigit13(prefix + isbn10);
      return prefix + isbn10 + checkDigit;
    }
    
    // 13자리 ISBN을 10자리로 변환
    if (cleanISBN.length === 13 && cleanISBN.startsWith('978')) {
      return cleanISBN.substring(3, 12) + calculateCheckDigit10(cleanISBN.substring(3, 12));
    }
    
    return cleanISBN;
  };

  // 13자리 ISBN 체크디지트 계산
  const calculateCheckDigit13 = (isbn12) => {
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      sum += parseInt(isbn12[i]) * (i % 2 === 0 ? 1 : 3);
    }
    const remainder = sum % 10;
    return remainder === 0 ? 0 : 10 - remainder;
  };

  // 10자리 ISBN 체크디지트 계산
  const calculateCheckDigit10 = (isbn9) => {
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(isbn9[i]) * (10 - i);
    }
    const remainder = sum % 11;
    return remainder === 0 ? '0' : remainder === 1 ? 'X' : (11 - remainder).toString();
  };

  // 특정 ISBN으로 책 찾기 (디버깅용)
  const findBookByISBN = (targetISBN) => {
    if (!optimizedUserBooks.length) {
      console.log("책 목록이 비어있습니다.");
      return null;
    }

    const normalizedTargetISBN = normalizeISBN(targetISBN);
    console.log(`찾고 있는 ISBN: ${targetISBN} → 정규화: ${normalizedTargetISBN}`);

    const foundBook = optimizedUserBooks.find(book => {
      const bookISBN = normalizeISBN(book.isbn);
      const isMatch = bookISBN === normalizedTargetISBN || book.isbn === targetISBN;
      
      if (isMatch) {
        console.log("매칭된 책 발견:", {
          id: book.id,
          title: book.title,
          originalISBN: book.isbn,
          normalizedISBN: bookISBN
        });
      }
      
      return isMatch;
    });

    if (!foundBook) {
      console.log("매칭되는 책을 찾을 수 없습니다.");
      console.log("현재 등록된 모든 책의 ISBN:");
      optimizedUserBooks.forEach((book, index) => {
        console.log(`${index + 1}. ${book.title}: ${book.isbn} → ${normalizeISBN(book.isbn)}`);
      });
    }

    return foundBook;
  };

  // 무한로딩을 위한 상태 추가
  const [displayedBooks, setDisplayedBooks] = useState([]);
  const [lastBook, setLastBook] = useState(null);
  const [hasMoreBooks, setHasMoreBooks] = useState(true);
  const [loadingBooks, setLoadingBooks] = useState(false);
  const [displayedHighlights, setDisplayedHighlights] = useState([]);
  const [lastDisplayedHighlight, setLastDisplayedHighlight] = useState(null);
  const [hasMoreDisplayedHighlights, setHasMoreDisplayedHighlights] = useState(true);
  const [loadingDisplayedHighlights, setLoadingDisplayedHighlights] = useState(false);
  
  // 페이지당 로드할 아이템 수 (더 많은 책을 한 번에 표시)
  const ITEMS_PER_PAGE = 30;

  // 게임 관리 관련 상태
  const [selectedTest, setSelectedTest] = useState(null);
  const [isTestEditDialogOpen, setIsTestEditDialogOpen] = useState(false);
  const [isTestDeleteDialogOpen, setIsTestDeleteDialogOpen] = useState(false);
  const [editedTestTitle, setEditedTestTitle] = useState('');
  const [testTitleError, setTestTitleError] = useState('');
  const [testActionLoading, setTestActionLoading] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setIsFollowing(false);
      setFollowLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchProfileUser = async () => {
      setPageLoading(true);
      setUserExists(true);
      
      const targetUserId = paramUserId || currentUser?.uid;

      if (!targetUserId) {
        setPageLoading(false);
        return;
      }

      try {
        const userDocRef = doc(db, 'users', targetUserId);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          const authUser = auth.currentUser;
          
          setProfileUser({
            uid: targetUserId,
            displayName: userData.displayName || (targetUserId === authUser?.uid ? authUser?.displayName : '익명'),
            photoURL: userData.photoURL || (targetUserId === authUser?.uid ? authUser?.photoURL : null),
          });
          setUserExists(true);
          setIsOwner(currentUser?.uid === targetUserId);
          setNewNickname(userData.displayName || (targetUserId === authUser?.uid ? authUser?.displayName : '익명'));
          setBio(userData.bio || "");
        } else {
          console.log(`Firestore에 사용자 문서 없음: ${targetUserId}`);
          
          // 첫 회원가입 후 즉시 접근한 경우, 잠시 대기 후 재시도
          if (currentUser && !paramUserId) {
            console.log("첫 회원가입 후 즉시 접근 감지, 잠시 대기 후 재시도...");
            setTimeout(async () => {
              try {
                const retryDocSnap = await getDoc(userDocRef);
                if (retryDocSnap.exists()) {
                  const userData = retryDocSnap.data();
                  const authUser = auth.currentUser;
                  
                  setProfileUser({
                    uid: targetUserId,
                    displayName: userData.displayName || (targetUserId === authUser?.uid ? authUser?.displayName : '익명'),
                    photoURL: userData.photoURL || (targetUserId === authUser?.uid ? authUser?.photoURL : null),
                  });
                  setUserExists(true);
                  setIsOwner(true);
                  setNewNickname(userData.displayName || (targetUserId === authUser?.uid ? authUser?.displayName : '익명'));
                  setBio(userData.bio || "");
                  setPageLoading(false);
                  setDataLoading(true);
                } else {
                  setProfileUser(null);
                  setUserExists(false);
                  setIsOwner(false);
                  setBio("");
                  setPageLoading(false);
                }
              } catch (retryError) {
                console.error("재시도 중 오류:", retryError);
                setProfileUser(null);
                setUserExists(false);
                setIsOwner(false);
                setBio("");
                setPageLoading(false);
              }
            }, 1000); // 1초 대기
            return;
          }
          
          setProfileUser(null);
          setUserExists(false);
          setIsOwner(false);
          setBio("");
        }
      } catch (error) {
        console.error("프로필 정보 로딩 오류:", error);
        setProfileUser(null);
        setUserExists(false);
        setIsOwner(false);
        setBio("");
      } finally {
        setPageLoading(false);
        setDataLoading(true);
      }
    };

    if (paramUserId || currentUser !== undefined) {
      fetchProfileUser();
    }

  }, [paramUserId, currentUser]);

  useEffect(() => {
    if (!isOwner && currentUser && profileUser) {
      setFollowLoading(true);
      const followDocRef = doc(db, 'users', currentUser.uid, 'following', profileUser.uid);
      getDoc(followDocRef)
        .then((docSnap) => {
          setIsFollowing(docSnap.exists());
        })
        .catch((error) => {
          console.error("Error checking follow status:", error);
          setIsFollowing(false);
        })
        .finally(() => {
          setFollowLoading(false);
        });
    } else {
      setIsFollowing(false);
    }
  }, [currentUser, profileUser, isOwner]);

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!profileUser?.uid || pageLoading) {
        setDataLoading(false);
        return;
      }
      
      console.log(`데이터 로딩 시작: ${profileUser.uid}`);
      setDataLoading(true);
      
      // 상태 초기화 - 중복 방지를 위해 명확하게 리셋
      setUserBooks([]);
      setBooksCount(0);
      setDisplayedBooks([]);
      setLastBook(null);
      setHasMoreBooks(true);
      setDisplayedHighlights([]);
      setLastDisplayedHighlight(null);
      setHasMoreDisplayedHighlights(true);

      try {
        const targetUid = profileUser.uid;

        // 책 목록 및 개수 가져오기 (모든 책을 한 번에 가져오기)
        const booksRef = collection(db, 'books');
        const booksQuery = query(
          booksRef,
          where('uid', '==', targetUid)
        );
        const booksSnapshot = await getDocs(booksQuery);
        const allBooks = booksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        console.log(`총 ${allBooks.length}개의 책 발견`);
        
        // 디버깅: 각 책의 정보 출력
        allBooks.forEach((book, index) => {
          const normalizedISBN = normalizeISBN(book.isbn);
          console.log(`책 ${index + 1}:`, {
            id: book.id,
            title: book.title,
            originalISBN: book.isbn,
            normalizedISBN: normalizedISBN,
            createdAt: book.createdAt,
            isPinned: book.isPinned
          });
        });
        
        // 클라이언트에서 정렬
        const sortedBooks = allBooks.sort((a, b) => {
          // 고정된 책을 먼저 정렬
          if (a.isPinned && !b.isPinned) return -1;
          if (!a.isPinned && b.isPinned) return 1;
          
          // 고정된 책들끼리는 pinnedAt으로 정렬
          if (a.isPinned && b.isPinned) {
            const aPinnedAt = a.pinnedAt?.toDate?.() || new Date(0);
            const bPinnedAt = b.pinnedAt?.toDate?.() || new Date(0);
            return bPinnedAt - aPinnedAt;
          }
          
          // 고정되지 않은 책들은 createdAt으로 정렬
          const aCreatedAt = a.createdAt?.toDate?.() || new Date(0);
          const bCreatedAt = b.createdAt?.toDate?.() || new Date(0);
          return bCreatedAt - aCreatedAt;
        });
        
        setUserBooks(sortedBooks);
        setBooksCount(sortedBooks.length);
        console.log(`${sortedBooks.length}개의 책 전체 개수 확인 (정렬 완료)`);
        
        // 디버깅: 특정 책 찾기 (9788966260577)
        setTimeout(() => {
          findBookByISBN('9788966260577');
        }, 1000);

        // 하이라이트 수만 가져오기
        const highlightsRef = collection(db, 'highlights');
        const highlightsCountQuery = query(highlightsRef, where('uid', '==', targetUid));
        const highlightsCountSnapshot = await getDocs(highlightsCountQuery);
        setHighlightsCount(highlightsCountSnapshot.size);
        console.log(`${highlightsCountSnapshot.size}개의 하이라이트 수 확인`);

      } catch (error) {
        console.error('데이터 로딩 오류:', error);
        
        // Firebase 인덱스 오류인 경우 더 구체적인 메시지
        if (error.code === 'failed-precondition' || error.message.includes('index')) {
          console.warn("초기 데이터 로딩 Firebase 인덱스 오류 감지, 단순화된 쿼리로 재시도...");
          try {
            // 단순화된 쿼리로 재시도
            const retryBooksRef = collection(db, 'books');
            const retryHighlightsRef = collection(db, 'highlights');
            const retryTargetUid = profileUser.uid;
            
            const simpleBooksQuery = query(
              retryBooksRef,
              where('uid', '==', retryTargetUid)
            );
            const simpleBooksSnapshot = await getDocs(simpleBooksQuery);
            const simpleAllBooks = simpleBooksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            console.log(`총 ${simpleAllBooks.length}개의 책 발견 (단순화된 쿼리)`);
            
            // 클라이언트에서 정렬
            const sortedSimpleBooks = simpleAllBooks.sort((a, b) => {
              if (a.isPinned && !b.isPinned) return -1;
              if (!a.isPinned && b.isPinned) return 1;
              
              if (a.isPinned && b.isPinned) {
                const aPinnedAt = a.pinnedAt?.toDate?.() || new Date(0);
                const bPinnedAt = b.pinnedAt?.toDate?.() || new Date(0);
                return bPinnedAt - aPinnedAt;
              }
              
              const aCreatedAt = a.createdAt?.toDate?.() || new Date(0);
              const bCreatedAt = b.createdAt?.toDate?.() || new Date(0);
              return bCreatedAt - aCreatedAt;
            });
            
            setUserBooks(sortedSimpleBooks);
            setBooksCount(sortedSimpleBooks.length);
            console.log(`${sortedSimpleBooks.length}개의 책 전체 개수 확인 (단순화된 쿼리, 정렬 완료)`);

            const simpleHighlightsQuery = query(retryHighlightsRef, where('uid', '==', retryTargetUid));
            const simpleHighlightsSnapshot = await getDocs(simpleHighlightsQuery);
            setHighlightsCount(simpleHighlightsSnapshot.size);
            console.log(`${simpleHighlightsSnapshot.size}개의 하이라이트 수 확인 (단순화된 쿼리)`);
          } catch (retryError) {
            console.error("초기 데이터 재시도 실패:", retryError);
            setSnackbar({ open: true, message: '데이터 로딩 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.', severity: 'error' });
          }
        } else {
          setSnackbar({ open: true, message: '데이터 로딩 중 오류가 발생했습니다.', severity: 'error' });
        }
      } finally {
         setDataLoading(false);
         console.log("데이터 로딩 프로세스 완료");
      }
    };

    if (!pageLoading && userExists) {
      fetchInitialData();
    } else if (!pageLoading && !userExists) {
      setDataLoading(false);
    }
  }, [pageLoading, profileUser?.uid, userExists]); 

  useEffect(() => {
    // Skip if no highlights
    if (!displayedHighlights.length) return;

    // Add debug log to understand highlight data structure
    console.log("Grouping highlights:", displayedHighlights);
    console.log("Current books:", userBooks);

    // Group highlights by bookId with improved matching
    const groupedHighlights = displayedHighlights.reduce((acc, highlight) => {
      // Extract bookId from highlight - could be different formats
      const bookId = highlight.bookId;
      const bookDocId = highlight.bookDocId; // Some highlights might use bookDocId
      
      if (!bookId && !bookDocId) {
        console.warn("Highlight missing bookId and bookDocId:", highlight);
        return acc;
      }
      
      // Create keys for different possible ID formats
      const possibleKeys = [];
      
      // Original bookId
      if (bookId) {
        possibleKeys.push(bookId);
        // Clean ISBN (remove spaces and other characters if needed)
        const cleanBookId = bookId.trim().replace(/\s+/g, '');
        if (cleanBookId !== bookId) possibleKeys.push(cleanBookId);
      }
      
      // BookDocId (may contain userId_bookId format)
      if (bookDocId) {
        possibleKeys.push(bookDocId);
        // Try to extract just the ISBN part if format is userId_bookId
        const parts = bookDocId.split('_');
        if (parts.length > 1) {
          possibleKeys.push(parts[1]); // Just the ISBN part
        }
      }
      
      // Use first key available as primary key for storing
      const primaryKey = possibleKeys[0];
      
      // Store under all possible keys for better matching later
      possibleKeys.forEach(key => {
        if (!acc[key]) {
          acc[key] = {
            bookId: primaryKey,
            bookDocId: bookDocId,
            originalIds: possibleKeys, // Store all IDs for debugging
            bookTitle: highlight.bookTitle || '제목 없음',
            bookThumbnail: highlight.bookThumbnail || '/images/no-cover.png',
            highlights: [],
          };
        }
        // Add highlight to all matching keys
        acc[key].highlights.push(highlight);
      });
      
      return acc;
    }, {});

    console.log("Grouped highlights by book:", groupedHighlights);
    setUserHighlightsByBook(groupedHighlights);
  }, [displayedHighlights, userBooks]);

  // 성능 최적화를 위한 메모이제이션
  const memoizedLoadMoreBooks = useCallback(async () => {
    if (!profileUser?.uid || loadingBooks || !hasMoreBooks || dataLoading) {
      return;
    }

    console.log("책 더 로드 시작", lastBook ? `after: ${lastBook.id}` : "first page");
    console.log("현재 사용자 ID:", profileUser.uid);
    setLoadingBooks(true);

    try {
      const booksRef = collection(db, 'books');
      
      // 첫 번째 페이지 로딩 - 모든 책을 한 번에 가져오기
      if (!lastBook) {
        console.log("첫 번째 페이지 로딩 - 모든 책 가져오기");
        
        // 모든 책을 가져오기 위해 제한 없이 쿼리 (인덱스 오류 방지)
        const baseQuery = query(
          booksRef,
          where('uid', '==', profileUser.uid)
        );
        
        const snapshot = await getDocs(baseQuery);
        const allBooks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        console.log(`총 ${allBooks.length}개의 책 발견`);
        console.log("Firebase 쿼리 결과:", {
          query: `uid == ${profileUser.uid}`,
          resultCount: snapshot.size,
          empty: snapshot.empty
        });
        
        // 클라이언트에서 정렬 및 페이지네이션
        const sortedBooks = allBooks.sort((a, b) => {
          // 고정된 책을 먼저 정렬
          if (a.isPinned && !b.isPinned) return -1;
          if (!a.isPinned && b.isPinned) return 1;
          
          // 고정된 책들끼리는 pinnedAt으로 정렬
          if (a.isPinned && b.isPinned) {
            const aPinnedAt = a.pinnedAt?.toDate?.() || new Date(0);
            const bPinnedAt = b.pinnedAt?.toDate?.() || new Date(0);
            return bPinnedAt - aPinnedAt;
          }
          
          // 고정되지 않은 책들은 createdAt으로 정렬
          const aCreatedAt = a.createdAt?.toDate?.() || new Date(0);
          const bCreatedAt = b.createdAt?.toDate?.() || new Date(0);
          return bCreatedAt - aCreatedAt;
        });
        
        // 첫 페이지만 표시
        const firstPageBooks = sortedBooks.slice(0, ITEMS_PER_PAGE);
        
        console.log(`첫 페이지 ${firstPageBooks.length}개 표시 (총 ${sortedBooks.length}개)`);
        
        setDisplayedBooks(firstPageBooks);
        setUserBooks(sortedBooks); // 전체 책 목록 저장
        setBooksCount(sortedBooks.length);
        
        // 더 로드할 책이 있는지 확인
        setHasMoreBooks(sortedBooks.length > ITEMS_PER_PAGE);
        
        // 마지막 책 정보 저장 (다음 페이지용)
        if (sortedBooks.length > ITEMS_PER_PAGE) {
          setLastBook({
            id: sortedBooks[ITEMS_PER_PAGE - 1].id,
            createdAt: sortedBooks[ITEMS_PER_PAGE - 1].createdAt,
            isPinned: sortedBooks[ITEMS_PER_PAGE - 1].isPinned,
            pinnedAt: sortedBooks[ITEMS_PER_PAGE - 1].pinnedAt,
            currentIndex: ITEMS_PER_PAGE - 1
          });
        } else {
          setLastBook(null);
          setHasMoreBooks(false);
        }
      } else {
        // 두 번째 페이지부터는 클라이언트 사이드 페이지네이션 사용
        console.log("추가 페이지 로딩 - 클라이언트 사이드 페이지네이션");
        
        // 이미 로드된 전체 책 목록에서 다음 페이지 가져오기
        const currentIndex = lastBook.currentIndex || 0;
        const nextIndex = currentIndex + 1;
        const endIndex = Math.min(nextIndex + ITEMS_PER_PAGE, optimizedUserBooks.length);
        
        if (nextIndex >= optimizedUserBooks.length) {
          console.log("더 이상 로드할 책 없음");
          setHasMoreBooks(false);
        } else {
          const nextPageBooks = optimizedUserBooks.slice(nextIndex, endIndex);
          console.log(`${nextPageBooks.length}개의 새 책 로드 완료 (${nextIndex + 1}-${endIndex}/${optimizedUserBooks.length})`);
          
          setDisplayedBooks(prev => [...prev, ...nextPageBooks]);
          
          // 더 로드할 책이 있는지 확인
          const hasMore = endIndex < optimizedUserBooks.length;
          setHasMoreBooks(hasMore);
          
          if (hasMore) {
            setLastBook({
              id: optimizedUserBooks[endIndex - 1].id,
              createdAt: optimizedUserBooks[endIndex - 1].createdAt,
              isPinned: optimizedUserBooks[endIndex - 1].isPinned,
              pinnedAt: optimizedUserBooks[endIndex - 1].pinnedAt,
              currentIndex: endIndex - 1
            });
          } else {
            setLastBook(null);
          }
        }
      }
    } catch (error) {
      console.error("책 로딩 오류:", error);
      
      // Firebase 인덱스 오류인 경우 더 구체적인 메시지
      if (error.code === 'failed-precondition' || error.message.includes('index')) {
        console.warn("Firebase 인덱스 오류 감지, 단순화된 쿼리로 재시도...");
        // 단순화된 쿼리로 재시도
        try {
          const retryBooksRef = collection(db, 'books');
          const simpleQuery = query(
            retryBooksRef,
            where('uid', '==', profileUser.uid)
          );
          const simpleSnapshot = await getDocs(simpleQuery);
          const simpleBooks = simpleSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          // 클라이언트에서 정렬
          const sortedSimpleBooks = simpleBooks.sort((a, b) => {
            if (a.isPinned && !b.isPinned) return -1;
            if (!a.isPinned && b.isPinned) return 1;
            
            if (a.isPinned && b.isPinned) {
              const aPinnedAt = a.pinnedAt?.toDate?.() || new Date(0);
              const bPinnedAt = b.pinnedAt?.toDate?.() || new Date(0);
              return bPinnedAt - aPinnedAt;
            }
            
            const aCreatedAt = a.createdAt?.toDate?.() || new Date(0);
            const bCreatedAt = b.createdAt?.toDate?.() || new Date(0);
            return bCreatedAt - aCreatedAt;
          });
          
          console.log(`${sortedSimpleBooks.length}개의 책 로드 완료 (단순화된 쿼리)`);
          
          // 첫 페이지만 표시
          const firstPageBooks = sortedSimpleBooks.slice(0, ITEMS_PER_PAGE);
          setDisplayedBooks(firstPageBooks);
          setUserBooks(sortedSimpleBooks);
          setBooksCount(sortedSimpleBooks.length);
          
          setHasMoreBooks(sortedSimpleBooks.length > ITEMS_PER_PAGE);
          if (sortedSimpleBooks.length > ITEMS_PER_PAGE) {
            setLastBook({
              id: sortedSimpleBooks[ITEMS_PER_PAGE - 1].id,
              createdAt: sortedSimpleBooks[ITEMS_PER_PAGE - 1].createdAt,
              isPinned: sortedSimpleBooks[ITEMS_PER_PAGE - 1].isPinned,
              pinnedAt: sortedSimpleBooks[ITEMS_PER_PAGE - 1].pinnedAt,
              currentIndex: ITEMS_PER_PAGE - 1
            });
          } else {
            setLastBook(null);
          }
        } catch (retryError) {
          console.error("재시도 실패:", retryError);
          setSnackbar({ open: true, message: '책 로딩 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.', severity: 'error' });
        }
      } else {
        setSnackbar({ open: true, message: '책 로딩 중 오류가 발생했습니다.', severity: 'error' });
      }
    } finally {
      setLoadingBooks(false);
    }
  }, [profileUser?.uid, loadingBooks, hasMoreBooks, lastBook, ITEMS_PER_PAGE, dataLoading]);

  // 하이라이트 뷰용 무한로딩 (기존 로직 개선)
  const memoizedLoadMoreHighlights = useCallback(async () => {
    if (currentView !== 'highlights') return;
    
    const targetUid = profileUser?.uid;
    if (!targetUid || loadingDisplayedHighlights || !hasMoreDisplayedHighlights || dataLoading) {
      return;
    }
    console.log("하이라이트 더 로드 시작", lastDisplayedHighlight ? `after: ${lastDisplayedHighlight.id}` : "first page");

    setLoadingDisplayedHighlights(true);
    try {
      const highlightsRef = collection(db, "highlights");
      let q;
      const baseQuery = [
        where("uid", "==", targetUid),
        orderBy("createdAt", "desc"),
        limit(ITEMS_PER_PAGE)
      ];
      if (lastDisplayedHighlight) {
        q = query(highlightsRef, ...baseQuery, startAfter(lastDisplayedHighlight));
      } else {
        q = query(highlightsRef, ...baseQuery);
      }
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        console.log("더 이상 로드할 하이라이트 없음");
        setHasMoreDisplayedHighlights(false);
      } else {
        const newHighlights = await Promise.all(
          snapshot.docs.map(async (highlightDoc) => {
            const data = highlightDoc.data();
            const originalBookId = data.bookId;

            console.log(`[Highlight ${highlightDoc.id}] Using data directly from highlight document.`);

            const bookTitle = data.bookTitle || "책 정보 없음 (제목 누락)";
            const bookThumbnail = data.bookThumbnail || "/images/no-cover.png";
            const bookAuthors = data.bookAuthors || [];
            const userName = data.userName || profileUser?.displayName || '익명';

            if (!data.bookTitle) {
                console.warn(`[Highlight ${highlightDoc.id}] bookTitle missing in highlight data.`);
            }
            
            if (!data.bookAuthors || data.bookAuthors.length === 0) {
                 console.warn(`[Highlight ${highlightDoc.id}] bookAuthors missing or empty in highlight data.`);
            }

            return {
              id: highlightDoc.id,
              text: data.text,
              bookId: originalBookId,
              createdAt: data.createdAt,
              bookTitle: bookTitle, 
              bookThumbnail: bookThumbnail,
              bookAuthors: bookAuthors,
              userName: userName 
            };
          })
        );
        console.log(`${newHighlights.length}개의 새 하이라이트 로드 완료`);
        
        // 중복 방지를 위한 필터링
        setDisplayedHighlights(prev => {
          const existingIds = new Set(prev.map(highlight => highlight.id));
          const uniqueNewHighlights = newHighlights.filter(highlight => !existingIds.has(highlight.id));
          
          if (uniqueNewHighlights.length !== newHighlights.length) {
            console.log(`하이라이트 중복 제거: ${newHighlights.length - uniqueNewHighlights.length}개`);
          }
          
          return [...prev, ...uniqueNewHighlights];
        });
        
        setLastDisplayedHighlight(snapshot.docs[snapshot.docs.length - 1]);
        setHasMoreDisplayedHighlights(snapshot.docs.length === ITEMS_PER_PAGE);
      }
    } catch (error) {
      console.error("하이라이트 로딩 오류:", error);
      
      // Firebase 인덱스 오류인 경우 더 구체적인 메시지
      if (error.code === 'failed-precondition' || error.message.includes('index')) {
        console.warn("하이라이트 Firebase 인덱스 오류 감지, 단순화된 쿼리로 재시도...");
        // 단순화된 쿼리로 재시도
        try {
          const retryHighlightsRef = collection(db, "highlights");
          const retryTargetUid = profileUser?.uid;
          const simpleQuery = query(
            retryHighlightsRef,
            where("uid", "==", retryTargetUid),
            orderBy("createdAt", "desc"),
            limit(ITEMS_PER_PAGE)
          );
          const simpleSnapshot = await getDocs(simpleQuery);
          const simpleHighlights = simpleSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              text: data.text,
              bookId: data.bookId,
              createdAt: data.createdAt,
              bookTitle: data.bookTitle || "책 정보 없음",
              bookThumbnail: data.bookThumbnail || "/images/no-cover.png",
              bookAuthors: data.bookAuthors || [],
              userName: data.userName || profileUser?.displayName || '익명'
            };
          });
          
          console.log(`${simpleHighlights.length}개의 하이라이트 로드 완료 (단순화된 쿼리)`);
          setDisplayedHighlights(simpleHighlights);
          setLastDisplayedHighlight(simpleSnapshot.docs[simpleSnapshot.docs.length - 1] || null);
          setHasMoreDisplayedHighlights(simpleSnapshot.docs.length === ITEMS_PER_PAGE);
        } catch (retryError) {
          console.error("하이라이트 재시도 실패:", retryError);
          setSnackbar({ open: true, message: '하이라이트 로딩 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.', severity: 'error' });
        }
      } else {
        setSnackbar({ open: true, message: '하이라이트 로딩 중 오류가 발생했습니다.', severity: 'error' });
      }
    } finally {
      setLoadingDisplayedHighlights(false);
    }
  }, [profileUser?.uid, loadingDisplayedHighlights, hasMoreDisplayedHighlights, lastDisplayedHighlight, currentView, ITEMS_PER_PAGE, dataLoading]);

  // 뷰 변경 시 해당 뷰의 초기 데이터 로드
  useEffect(() => {
    // dataLoading 중에는 실행하지 않음
    if (dataLoading) return;
    
    if (currentView === 'grid' || currentView === 'list') {
      // 그리드/리스트 뷰: 책 데이터 초기 로드
      if (displayedBooks.length === 0 && hasMoreBooks && !loadingBooks) {
        console.log("Grid/List view activated, loading initial books...");
        // 약간의 지연을 두어 상태 업데이트가 완료된 후 로딩 시작
        setTimeout(() => {
          memoizedLoadMoreBooks();
        }, 0);
      } else if (displayedBooks.length === 0 && optimizedUserBooks.length > 0) {
        // 이미 로드된 책이 있지만 표시되지 않은 경우
        console.log("Books already loaded, displaying first page...");
        const firstPageBooks = optimizedUserBooks.slice(0, ITEMS_PER_PAGE);
        setDisplayedBooks(firstPageBooks);
        setHasMoreBooks(optimizedUserBooks.length > ITEMS_PER_PAGE);
        if (optimizedUserBooks.length > ITEMS_PER_PAGE) {
          setLastBook({
            id: optimizedUserBooks[ITEMS_PER_PAGE - 1].id,
            createdAt: optimizedUserBooks[ITEMS_PER_PAGE - 1].createdAt,
            isPinned: optimizedUserBooks[ITEMS_PER_PAGE - 1].isPinned,
            pinnedAt: optimizedUserBooks[ITEMS_PER_PAGE - 1].pinnedAt,
            currentIndex: ITEMS_PER_PAGE - 1
          });
        }
      }
    } else if (currentView === 'highlights') {
      // 하이라이트 뷰: 하이라이트 데이터 초기 로드
      if (displayedHighlights.length === 0 && hasMoreDisplayedHighlights && !loadingDisplayedHighlights) {
        console.log("Highlights view activated, loading initial highlights...");
        // 상태를 명확하게 리셋
        setDisplayedHighlights([]);
        setLastDisplayedHighlight(null);
        setHasMoreDisplayedHighlights(true);
        // 약간의 지연을 두어 상태 업데이트가 완료된 후 로딩 시작
        setTimeout(() => {
          memoizedLoadMoreHighlights();
        }, 0);
      }
    }
  }, [currentView, displayedBooks.length, displayedHighlights.length, hasMoreBooks, hasMoreDisplayedHighlights, memoizedLoadMoreBooks, memoizedLoadMoreHighlights, loadingBooks, loadingDisplayedHighlights, dataLoading]);



  // 컴포넌트 언마운트 시 cleanup
  useEffect(() => {
    return () => {
      // 메모리 누수 방지를 위한 cleanup
      setDisplayedBooks([]);
      setDisplayedHighlights([]);
      setLastBook(null);
      setLastDisplayedHighlight(null);
    };
  }, []);

  const handleShareLibrary = () => {
    if (!profileUser?.uid) return;
    setIsShareDialogOpen(true);
  };

  const handleCopyLink = () => {
    if (!profileUser?.uid) return;
    const shareUrl = `${window.location.origin}/u/${profileUser.uid}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setSnackbar({ open: true, message: '사용자 서재 링크가 복사되었습니다!', severity: 'success' });
      setIsShareDialogOpen(false);
    });
  };

  const handleInstagramShare = () => {
    if (!profileUser?.uid) return;
    const shareUrl = `${window.location.origin}/u/${profileUser.uid}`;
    window.open(`https://www.instagram.com/direct/new/?text=${encodeURIComponent(shareUrl)}`);
    setIsShareDialogOpen(false);
  };

  const handleShareKakao = () => {
    if (!profileUser?.uid) return;
    if (window.Kakao) {
      const kakao = window.Kakao;
      
      if (!kakao.isInitialized()) {
        kakao.init('0540ea1b773afa38314d580989ae46de');
      }
      
      const pageUrl = `${window.location.origin}/u/${profileUser.uid}`;

      kakao.Share.sendDefault({
        objectType: 'feed',
        content: {
          title: `${profileUser?.displayName || '사용자'}님의 서재`,
          description: '서재를 구경해보세요!',
          imageUrl: profileUser?.photoURL || 'https://via.placeholder.com/150',
          link: {
            mobileWebUrl: pageUrl,
            webUrl: pageUrl,
          },
        },
        buttons: [
          {
            title: '서재 구경하기',
            link: {
              mobileWebUrl: pageUrl,
              webUrl: pageUrl,
            },
          },
        ],
      });
    } else {
      setSnackbar({ open: true, message: '카카오톡 SDK 로드 실패 또는 초기화 오류.', severity: 'error' });
    }
    setIsShareDialogOpen(false);
  };

  const validateNickname = (nickname) => {
    if (!nickname) return "닉네임을 입력해주세요.";
    if (nickname.length > 10) return "닉네임은 10자 이내로 입력해주세요.";
    if (nickname.includes(" ")) return "닉네임에는 띄어쓰기를 사용할 수 없습니다.";
    if (!/^[가-힣ㄱ-ㅎㅏ-ㅣa-zA-Z0-9]+$/.test(nickname)) {
      return "한글(자/모음 가능), 영문, 숫자만 사용 가능 (공백/특수문자 불가)";
    }
    return "";
  };

  const handleNicknameChange = (e) => {
    const value = e.target.value;
    setNewNickname(value);
    setNicknameError(validateNickname(value));
  };

  const handleProfileEdit = async () => {
    if (!isOwner || !currentUser) return;

    const error = validateNickname(newNickname);
    if (error) {
      setNicknameError(error);
      return;
    }
    if (newNickname === profileUser?.displayName) {
        setIsNicknameDialogOpen(false);
        return;
    }

    const userRef = doc(db, 'users', profileUser.uid);

    try {
      await updateProfile(auth.currentUser, {
        displayName: newNickname
      });

      try {
        const userDocSnap = await getDoc(userRef);
        if (userDocSnap.exists()) {
          await updateDoc(userRef, {
            displayName: newNickname,
            updatedAt: serverTimestamp()
          });
        } else {
          await setDoc(userRef, {
            displayName: newNickname,
            email: currentUser.email,
            photoURL: currentUser.photoURL,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          console.warn(`Firestore 사용자 문서가 없어 새로 생성: ${profileUser.uid}`);
        }
        setProfileUser(prev => ({ ...prev, displayName: newNickname }));
        setIsNicknameDialogOpen(false);
        setSnackbar({ open: true, message: '닉네임이 수정되었습니다.', severity: 'success' });

      } catch (firestoreError) {
        console.error('Firestore 사용자 문서 업데이트/생성 오류:', firestoreError);
        setSnackbar({ open: true, message: '닉네임 업데이트 중 일부 오류가 발생했습니다. (Firestore)', severity: 'warning' });
        setIsNicknameDialogOpen(false);
      }

    } catch (authError) {
      console.error('Firebase Auth 닉네임 수정 오류:', authError);
      setSnackbar({ open: true, message: '닉네임 수정 중 오류가 발생했습니다. (Auth)', severity: 'error' });
    }
  };

  const handleCancelEdit = () => {
    setIsNicknameDialogOpen(false);
    setNewNickname(profileUser?.displayName || '');
    setNicknameError("");
  };

  const handleOpenNicknameDialog = () => {
    if (!isOwner) return;
    navigate('/mypage/nickname-edit');
  };

  const handleEditHighlight = async () => {
    if (!isOwner || !selectedHighlight || !editedText.trim()) return;

    try {
      const highlightRef = doc(db, 'highlights', selectedHighlight.id);
      await updateDoc(highlightRef, {
        text: editedText.trim(),
        searchableText: editedText.trim().toLowerCase(),
        updatedAt: serverTimestamp()
      });

      // displayedHighlights 상태 업데이트
      setDisplayedHighlights(prev => prev.map(h => 
        h.id === selectedHighlight.id 
          ? { ...h, text: editedText.trim() } 
          : h
      ));

      setSnackbar({
        open: true,
        message: '하이라이트가 수정되었습니다.',
        severity: 'success'
      });
      setEditDialogOpen(false);
      setSelectedHighlight(null);
      setAnchorEl(null);
    } catch (error) {
      console.error('하이라이트 수정 오류:', error);
      setSnackbar({
        open: true,
        message: '하이라이트 수정 중 오류가 발생했습니다.',
        severity: 'error'
      });
    }
  };

  const handleDeleteHighlight = async () => {
    if (!isOwner || !selectedHighlight) return;

    try {
      await deleteDoc(doc(db, 'highlights', selectedHighlight.id));
      
      // displayedHighlights 상태에서 제거
      setDisplayedHighlights(prev => prev.filter(h => h.id !== selectedHighlight.id));
      setHighlightsCount(prev => prev - 1); 

      setSnackbar({
        open: true,
        message: '하이라이트가 삭제되었습니다.',
        severity: 'success'
      });
      setAnchorEl(null);
      setSelectedHighlight(null);

    } catch (error) {
      console.error('하이라이트 삭제 오류:', error);
      setSnackbar({
        open: true,
        message: '하이라이트 삭제 중 오류가 발생했습니다.',
        severity: 'error'
      });
    }
  };

  const handleOpenHighlightMenu = (event, highlight) => {
    if (!isOwner) return;
    setAnchorEl(event.currentTarget);
    setSelectedHighlight(highlight);
  };

  const handleEditHighlightMenu = () => {
    if (!isOwner || !selectedHighlight) return;
    navigate(`/highlight/edit/${selectedHighlight.id}`);
    setAnchorEl(null);
    setSelectedHighlight(null);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('로그아웃 오류:', error);
    }
  };

  const handleFollowToggle = async () => {
    if (!currentUser) {
      setSnackbar({ open: true, message: '로그인이 필요합니다.', severity: 'info' });
      return;
    }

    if (isOwner || !profileUser) {
      console.error("Cannot follow self or invalid profile user.");
      return;
    }

    setFollowLoading(true);
    const followDocRef = doc(db, 'users', currentUser.uid, 'following', profileUser.uid);

    try {
      if (isFollowing) {
        await deleteDoc(followDocRef);
        setIsFollowing(false);
        setSnackbar({ open: true, message: '언팔로우했습니다.', severity: 'success' });
        console.log(`User ${currentUser.uid} unfollowed ${profileUser.uid}`);
      } else {
        await setDoc(followDocRef, {
          followedAt: serverTimestamp(),
        });
        setIsFollowing(true);
        setSnackbar({ open: true, message: '팔로우했습니다.', severity: 'success' });
        console.log(`User ${currentUser.uid} followed ${profileUser.uid}`);
      }
    } catch (error) {
      console.error("Follow/Unfollow Error:", error);
      setSnackbar({ open: true, message: '작업 중 오류가 발생했습니다.', severity: 'error' });
    } finally {
      setFollowLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser && isOwner) {
      const followingRef = collection(db, 'users', currentUser.uid, 'following');
      const unsubscribe = onSnapshot(followingRef, (snapshot) => {
        setFollowingCount(snapshot.size);
      }, (error) => {
        console.error("Error fetching following count:", error);
        setSnackbar({ open: true, message: '팔로잉 수를 가져오는 중 오류 발생', severity: 'error' });
      });
      return () => unsubscribe();
    } else {
      // 다른 사람 프로필 보거나 로그인 안했을 땐 0으로 초기화
      setFollowingCount(0);
    }
  }, [currentUser, isOwner]);

  const handleOpenFollowingList = async () => {
    if (!currentUser || !isOwner) return;

    setIsFollowingListOpen(true);
    setIsFollowingListLoading(true);
    setFollowingList([]); // 목록 초기화

    try {
      const followingRef = collection(db, 'users', currentUser.uid, 'following');
      const followingSnapshot = await getDocs(followingRef);

      if (followingSnapshot.empty) {
        setIsFollowingListLoading(false);
        return;
      }

      const followingIds = followingSnapshot.docs.map(doc => doc.id);
      const userPromises = followingIds.map(id => getDoc(doc(db, 'users', id)));
      const userDocs = await Promise.all(userPromises);

      const fetchedFollowingList = userDocs
        .filter(docSnap => docSnap.exists()) // 존재하는 사용자 문서만 필터링
        .map(docSnap => ({
          id: docSnap.id,
          displayName: docSnap.data().displayName || '익명',
          photoURL: docSnap.data().photoURL || null,
          bio: docSnap.data().bio || '', // bio(소개메세지) 추가
        }));

      setFollowingList(fetchedFollowingList);

    } catch (error) {
      console.error("Error fetching following list:", error);
      setSnackbar({ open: true, message: '팔로잉 목록을 가져오는 중 오류 발생', severity: 'error' });
    } finally {
      setIsFollowingListLoading(false);
    }
  };

  const handleCloseFollowingList = () => {
    setIsFollowingListOpen(false);
  };

  // Add new useEffect to fetch highlight counts when userBooks are loaded
  useEffect(() => {
    // Skip if no user or no books
    if (!profileUser?.uid || !userBooks.length || dataLoading) {
      return;
    }

    console.log("Fetching highlight counts for user books");
    
    // Function to get highlight count for a book
    const fetchHighlightCountForBook = async (book) => {
      try {
        const highlightsRef = collection(db, 'highlights');
        // Different possible ID formats for the book
        const bookId = book.id;
        const isbn = book.isbn;
        const potentialIds = [bookId];
        
        // Add ISBN if available
        if (isbn) potentialIds.push(isbn);

        // Try to extract ISBN from bookId if it's in userId_isbn format
        if (bookId && bookId.includes('_')) {
          const parts = bookId.split('_');
          if (parts.length > 1) {
            potentialIds.push(parts[1]);
          }
        }
        
        // To avoid multiple queries, prepare a compound query
        // First, query for exact bookId match
        let q = query(
          highlightsRef,
          where('uid', '==', profileUser.uid),
          where('bookId', '==', bookId)
        );
        
        const snapshot = await getDocs(q);
        let count = snapshot.size;
        
        // If ISBN is different from bookId, also check for that
        if (isbn && isbn !== bookId) {
          q = query(
            highlightsRef,
            where('uid', '==', profileUser.uid),
            where('bookId', '==', isbn)
          );
          const isbnSnapshot = await getDocs(q);
          count += isbnSnapshot.size;
        }
        
        // Query for bookDocId match
        const bookDocId = bookId; // bookId is often the bookDocId
        if (bookDocId) {
          q = query(
            highlightsRef,
            where('uid', '==', profileUser.uid),
            where('bookDocId', '==', bookDocId)
          );
          const bookDocSnapshot = await getDocs(q);
          count += bookDocSnapshot.size;
        }
        
        // For each extracted part from bookId, also check
        for (const part of potentialIds) {
          if (part && part !== bookId && part !== isbn) {
            // Check if this part is used as bookId
            q = query(
              highlightsRef,
              where('uid', '==', profileUser.uid),
              where('bookId', '==', part)
            );
            const partSnapshot = await getDocs(q);
            count += partSnapshot.size;
            
            // Also check if used as bookDocId
            q = query(
              highlightsRef,
              where('uid', '==', profileUser.uid),
              where('bookDocId', '==', part)
            );
            const partDocSnapshot = await getDocs(q);
            count += partDocSnapshot.size;
          }
        }
        
        return { bookId, count };
      } catch (error) {
        console.error(`Error fetching highlights for book ${book.id}:`, error);
        return { bookId: book.id, count: 0 };
      }
    };
    
    // Process all books
    const fetchAllHighlightCounts = async () => {
      try {
        const results = await Promise.all(
          userBooks.map(book => fetchHighlightCountForBook(book))
        );
        
        // Convert results to an object keyed by bookId
        const counts = results.reduce((acc, result) => {
          acc[result.bookId] = result.count;
          return acc;
        }, {});
        
        console.log("Highlight counts by book:", counts);
        setBookHighlightCounts(counts);
      } catch (error) {
        console.error("Error fetching highlight counts:", error);
      }
    };
    
    // Fetch counts when books are loaded or view changes to list
    fetchAllHighlightCounts();
    
  }, [profileUser?.uid, userBooks, dataLoading]);

  const handleLogin = async () => {
    try {
      const result = await handleGoogleLogin(
        // 성공 콜백
        async (user) => {
          // 로그인 성공 후 Firestore users 컬렉션 확인 및 생성
          if (user) {
            const userDocRef = doc(db, "users", user.uid);
            const userDocSnap = await getDoc(userDocRef);
            
            if (!userDocSnap.exists()) {
              // 첫 회원가입인 경우 Firestore에 사용자 문서 생성 후 Home으로 리다이렉트
              console.log(`Firestore에 새 사용자 등록: ${user.uid}`);
              try {
                await setDoc(userDocRef, {
                  displayName: user.displayName,
                  email: user.email,
                  photoURL: user.photoURL,
                  createdAt: serverTimestamp(),
                  updatedAt: serverTimestamp()
                });
                setSnackbar({ open: true, message: '회원가입이 완료되었습니다!', severity: 'success' });
                // 첫 회원가입 시 Home으로 리다이렉트
                navigate('/');
              } catch (firestoreError) {
                console.error("Firestore 사용자 문서 생성 오류:", firestoreError);
                setSnackbar({ open: true, message: '사용자 정보 저장 중 오류 발생', severity: 'error' });
              }
            } else {
              console.log(`기존 사용자 로그인: ${user.uid}`);
              setSnackbar({ open: true, message: '로그인 되었습니다.', severity: 'success' });
            }
          }
        },
        // 오류 콜백
        (error) => {
          console.error("로그인 오류:", error);
          // Google 로그인 팝업 취소 등도 에러로 잡힐 수 있음
          if (error.code !== 'auth/popup-closed-by-user') {
            setSnackbar({ open: true, message: '로그인 중 오류가 발생했습니다.', severity: 'error' });
          }
        },
        // 인앱 브라우저 감지 콜백
        () => {
          setInAppBrowserModalOpen(true);
        }
      );
    } catch (error) {
      console.error("로그인 프로세스 오류:", error);
      setSnackbar({ open: true, message: '로그인 중 오류가 발생했습니다.', severity: 'error' });
    }
  };

  // 팔로워 수 가져오기
  useEffect(() => {
    if (currentUser && isOwner) {
      const followersRef = collection(db, 'users');
      // followers: 나를 팔로우하는 사람의 수 = users/*/following/{내uid} 문서가 존재하는 user의 수
      const fetchFollowers = async () => {
        try {
          const usersSnapshot = await getDocs(followersRef);
          let count = 0;
          for (const userDoc of usersSnapshot.docs) {
            const followingRef = collection(db, 'users', userDoc.id, 'following');
            const followingSnapshot = await getDocs(followingRef);
            if (followingSnapshot.docs.some(doc => doc.id === currentUser.uid)) {
              count++;
            }
          }
          setFollowerCount(count);
        } catch (e) {
          setFollowerCount(0);
        }
      };
      fetchFollowers();
    } else {
      setFollowerCount(0);
    }
  }, [currentUser, isOwner]);

  // 스냅리스트 바텀시트 표시 여부 확인
  useEffect(() => {
    if (!profileUser?.uid || isOwner || !paramUserId) return;

    const checkSnapFollowSheet = async () => {
      // 오늘 날짜 확인
      const today = new Date().toISOString().split('T')[0];
      const hideUntil = localStorage.getItem('hideSnapFollowPrompt');

      // 이미 팔로우 중인지 확인
      if (currentUser) {
        const followDocRef = doc(db, 'users', currentUser.uid, 'following', profileUser.uid);
        const followDocSnap = await getDoc(followDocRef);
        if (followDocSnap.exists()) return;
      }

      // 하루 동안 보지 않기 설정이 없거나 만료된 경우에만 표시
      if (!hideUntil || hideUntil !== today) {
        setShowSnapFollowSheet(true);
      }
    };

    checkSnapFollowSheet();
  }, [profileUser, currentUser, isOwner, paramUserId]);

  // 게임 배너 표시 조건 확인
  useEffect(() => {
    if (!isOwner || !currentUser) {
      setShowGameBanner(false);
      return;
    }

    const checkGameBannerCondition = () => {
      const minBooks = 3; // 최소 3권의 책
      const minHighlights = 5; // 최소 5개의 하이라이트
      
      const hasEnoughBooks = userBooks.length >= minBooks;
      const hasEnoughHighlights = highlightsCount >= minHighlights;
      
      setShowGameBanner(true);
      setGameBannerDisabled(!hasEnoughBooks || !hasEnoughHighlights);
    };

    checkGameBannerCondition();
  }, [isOwner, currentUser, userBooks.length, highlightsCount]);

  // 사용자 테스트 불러오기
  useEffect(() => {
    if (!isOwner || !currentUser) {
      setUserTests([]);
      return;
    }

    const fetchUserTests = async () => {
      try {
        setTestLoading(true);
        const testsRef = collection(db, 'tests');
        const q = query(testsRef, where('creatorId', '==', currentUser.uid));
        const snapshot = await getDocs(q);
        
        const tests = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setUserTests(tests);
      } catch (err) {
        console.error('테스트 불러오기 오류:', err);
      } finally {
        setTestLoading(false);
      }
    };

    fetchUserTests();
  }, [isOwner, currentUser]);

  // window 스크롤 이벤트로 무한로딩 트리거
  useEffect(() => {
    const handleWindowScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrollPercentage = docHeight > 0 ? scrollY / docHeight : 0;
      if (scrollPercentage >= 0.8) {
        if (currentView === 'grid' || currentView === 'list') {
          if (!loadingBooks && hasMoreBooks) {
            memoizedLoadMoreBooks();
          }
        } else if (currentView === 'highlights') {
          if (!loadingDisplayedHighlights && hasMoreDisplayedHighlights) {
            memoizedLoadMoreHighlights();
          }
        }
      }
    };
    window.addEventListener('scroll', handleWindowScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, [currentView, loadingBooks, hasMoreBooks, loadingDisplayedHighlights, hasMoreDisplayedHighlights, memoizedLoadMoreBooks, memoizedLoadMoreHighlights]);

  // 게임 관련 핸들러 함수들
  const handleCreateGame = () => {
    if (gameBannerDisabled) {
      setSnackbar({ 
        open: true, 
        message: `문장 ${Math.max(5 - highlightsCount, 0)}개, 책 ${Math.max(3 - userBooks.length, 0)}권 더 등록하면 나만의 퀴즈를 만들 수 있어요!`, 
        severity: 'info' 
      });
      return;
    }
    navigate('/game/create');
  };

  const handlePlayTest = (testId) => {
    navigate(`/game/play/${testId}`);
  };

  const handleShareTest = useCallback((testId) => {
    const test = userTests.find(t => t.id === testId);
    if (test) {
      const shareUrl = `${window.location.origin}/game/play/${testId}`;
      const shareText = `${generateKitchyTitle(test.title, test.creatorName, testId)} - 나의 하이라이트 테스트에 도전해보세요!`;
      
      // 모바일 기기인 경우 네이티브 공유 기능 사용
      if (isMobileDevice() && navigator.share) {
        navigator.share({
          title: generateKitchyTitle(test.title, test.creatorName, testId),
          text: shareText,
          url: shareUrl
        }).catch((error) => {
          // 네이티브 공유가 실패하면 클립보드 복사로 폴백
          fallbackToClipboard(shareText, shareUrl);
        });
      } else {
        // PC 또는 네이티브 공유가 지원되지 않는 경우 클립보드 복사
        fallbackToClipboard(shareText, shareUrl);
      }
    }
  }, [userTests]);

  // 클립보드 복사 폴백 함수
  const fallbackToClipboard = useCallback(async (shareText, shareUrl) => {
    try {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      setSnackbar({ 
        open: true, 
        message: '🎯 테스트 링크가 복사되었어요!', 
        severity: 'success' 
      });
    } catch (error) {
      // 클립보드 API가 지원되지 않는 경우 수동 복사 안내
      const textToCopy = `${shareText}\n${shareUrl}`;
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setSnackbar({ 
          open: true, 
          message: '🎯 테스트 링크가 복사되었어요!', 
          severity: 'success' 
        });
      } catch (fallbackError) {
        setSnackbar({ 
          open: true, 
          message: '❌ 링크 복사에 실패했어요. 수동으로 복사해주세요.', 
          severity: 'error' 
        });
      }
      document.body.removeChild(textArea);
    }
  }, []);

  // 키치한 테스트 제목 생성 함수
  const generateKitchyTitle = (originalTitle, creatorName, testId = '') => {
    // 사용자가 직접 입력한 제목이 있으면 그대로 사용
    if (originalTitle && !originalTitle.includes('님의 하이라이트 테스트')) {
      return originalTitle;
    }

    // 키치한 제목 템플릿들
    const kitchyTemplates = [
    '제 {N}차 독서 경연대회',
    '제 {N}차 전투력 측정대회',
    '제 {N}차 천하제일 무술대회',
    '제 {N}차 독서왕 선발대회',
    '제 {N}차 책벌레 경마',
    '제 {N}차 독서 서커스',
    '제 {N}차 책 제목 찍기 대회',
    '제 {N}차 독서 카니발',
    '제 {N}차 취향 맞추기'
    ];

    // testId를 기반으로 일관된 템플릿과 회차 선택
    if (testId) {
      const hash = testId.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
      
      // 템플릿 선택
      const templateIndex = Math.abs(hash) % kitchyTemplates.length;
      const template = kitchyTemplates[templateIndex];
      
      // 회차 생성 (1~99회)
      const roundNumber = (Math.abs(hash) % 99) + 1;
      
      return template.replace('{N}', roundNumber);
    }

    // testId가 없으면 랜덤 선택
    const randomTemplateIndex = Math.floor(Math.random() * kitchyTemplates.length);
    const randomTemplate = kitchyTemplates[randomTemplateIndex];
    const randomRound = Math.floor(Math.random() * 99) + 1;
    
    return randomTemplate.replace('{N}', randomRound);
  };

  // 게임 제목 수정 핸들러
  const handleEditTestTitle = (test) => {
    setSelectedTest(test);
    setEditedTestTitle(test.title);
    setTestTitleError('');
    setIsTestEditDialogOpen(true);
  };

  // 다이얼로그 열릴 때 TextField에 포커스
  useEffect(() => {
    if (isTestEditDialogOpen) {
      const timer = setTimeout(() => {
        const textField = document.querySelector('input[placeholder="테스트 제목을 입력하세요"]');
        if (textField) {
          textField.focus();
          textField.select(); // 기존 텍스트 선택
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isTestEditDialogOpen]);

  // 게임 삭제 핸들러
  const handleDeleteTest = (test) => {
    setSelectedTest(test);
    setIsTestDeleteDialogOpen(true);
  };

  // 게임 제목 수정 실행
  const handleSaveTestTitle = async () => {
    if (!selectedTest || !editedTestTitle.trim()) {
      setTestTitleError('제목을 입력해주세요.');
      return;
    }

    if (editedTestTitle.length > 50) {
      setTestTitleError('제목은 50자 이하여야 합니다.');
      return;
    }

    setTestActionLoading(true);
    try {
      const testRef = doc(db, 'tests', selectedTest.id);
      await updateDoc(testRef, {
        title: editedTestTitle.trim()
      });

      // 로컬 상태 업데이트
      setUserTests(prev => prev.map(test => 
        test.id === selectedTest.id 
          ? { ...test, title: editedTestTitle.trim() }
          : test
      ));

      setSnackbar({ 
        open: true, 
        message: '테스트 제목이 수정되었습니다!', 
        severity: 'success' 
      });
      setIsTestEditDialogOpen(false);
    } catch (error) {
      console.error('테스트 제목 수정 오류:', error);
      setSnackbar({ 
        open: true, 
        message: '테스트 제목 수정 중 오류가 발생했습니다.', 
        severity: 'error' 
      });
    } finally {
      setTestActionLoading(false);
    }
  };

  // 게임 삭제 실행
  const handleConfirmDeleteTest = async () => {
    if (!selectedTest) return;

    setTestActionLoading(true);
    try {
      const testRef = doc(db, 'tests', selectedTest.id);
      await deleteDoc(testRef);

      // 로컬 상태 업데이트
      setUserTests(prev => prev.filter(test => test.id !== selectedTest.id));

      setSnackbar({ 
        open: true, 
        message: '테스트가 삭제되었습니다!', 
        severity: 'success' 
      });
      setIsTestDeleteDialogOpen(false);
    } catch (error) {
      console.error('테스트 삭제 오류:', error);
      setSnackbar({ 
        open: true, 
        message: '테스트 삭제 중 오류가 발생했습니다.', 
        severity: 'error' 
      });
    } finally {
      setTestActionLoading(false);
    }
  };

  // 게임 제목 유효성 검사
  const validateTestTitle = (title) => {
    if (!title.trim()) {
      return '제목을 입력해주세요.';
    }
    if (title.length > 50) {
      return '제목은 50자 이하여야 합니다.';
    }
    return '';
  };

  // 게임 제목 변경 핸들러
  const handleTestTitleChange = (e) => {
    const newTitle = e.target.value;
    setEditedTestTitle(newTitle);
    setTestTitleError(validateTestTitle(newTitle));
  };

  // 랭킹 데이터 계산 함수
  const calculateRankingData = useCallback(async () => {
    // 안전성 검사 강화
    if (!profileUser || !userBooks || !Array.isArray(userBooks) || userBooks.length === 0) {
      console.log('랭킹 계산 조건 미충족:', { profileUser: !!profileUser, userBooks: !!userBooks, isArray: Array.isArray(userBooks), length: userBooks?.length });
      return;
    }
    
    setRankingLoading(true);
    try {
      // 1. 별점 기준 상위 책들 (5점 > 4점 > 3점 순)
      const topRatedBooks = [...userBooks]
        .filter(book => book && book.starRating && book.starRating > 0)
        .sort((a, b) => {
          if (b.starRating !== a.starRating) {
            return b.starRating - a.starRating;
          }
          // 별점이 같으면 하이라이트 수로 정렬
          const aHighlights = (bookHighlightCounts && bookHighlightCounts[a.id]) || 0;
          const bHighlights = (bookHighlightCounts && bookHighlightCounts[b.id]) || 0;
          return bHighlights - aHighlights;
        })
        .slice(0, 10);

      // 2. 하이라이트 수 기준 상위 책들
      const mostHighlightedBooks = [...userBooks]
        .filter(book => book && bookHighlightCounts && bookHighlightCounts[book.id] > 0)
        .sort((a, b) => {
          const aCount = (bookHighlightCounts && bookHighlightCounts[a.id]) || 0;
          const bCount = (bookHighlightCounts && bookHighlightCounts[b.id]) || 0;
          if (bCount !== aCount) {
            return bCount - aCount;
          }
          // 하이라이트 수가 같으면 별점으로 정렬
          return (b.starRating || 0) - (a.starRating || 0);
        })
        .slice(0, 10);

      // 3. 선호 장르/태그 분석
      const emotionCount = {};
      const purposeCount = {};
      const ownershipCount = {};
      
      userBooks.forEach(book => {
        if (!book) return; // book 객체 안전성 검사
        
        // 감정 태그 집계
        if (book.emotions && Array.isArray(book.emotions)) {
          book.emotions.forEach(emotion => {
            if (emotion && typeof emotion === 'string') {
              emotionCount[emotion] = (emotionCount[emotion] || 0) + 1;
            }
          });
        }
        
        // 읽은 이유 집계 (readingPurpose 또는 mustRead)
        let purposes = [];
        if (book.readingPurpose) {
          purposes = Array.isArray(book.readingPurpose) ? book.readingPurpose : [book.readingPurpose];
        } else if (book.mustRead) {
          purposes = Array.isArray(book.mustRead) ? book.mustRead : [book.mustRead];
        }
        
        if (Array.isArray(purposes)) {
          purposes.forEach(purpose => {
            if (purpose && typeof purpose === 'string') {
              purposeCount[purpose] = (purposeCount[purpose] || 0) + 1;
            }
          });
        }
        
        // 소장 여부 집계
        let ownership = [];
        if (book.ownershipStatus) {
          ownership = Array.isArray(book.ownershipStatus) ? book.ownershipStatus : [book.ownershipStatus];
        } else if (book.genres) {
          ownership = Array.isArray(book.genres) ? book.genres : [book.genres];
        }
        
        if (Array.isArray(ownership)) {
          ownership.forEach(own => {
            if (own && typeof own === 'string') {
              ownershipCount[own] = (ownershipCount[own] || 0) + 1;
            }
          });
        }
      });

      // 상위 태그들 추출
      const topEmotions = Object.entries(emotionCount)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5);
      
      const topPurposes = Object.entries(purposeCount)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5);
        
      const topOwnership = Object.entries(ownershipCount)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5);

      // 4. 독서 통계 (안전성 검사 포함)
      const ratedBooks = userBooks.filter(book => book && book.starRating && book.starRating > 0);
      const averageRating = ratedBooks.length > 0 
        ? ratedBooks.reduce((sum, book) => sum + book.starRating, 0) / ratedBooks.length
        : 0;

      const readingStats = {
        totalBooks: userBooks.length || 0,
        totalHighlights: highlightsCount || 0,
        averageRating: averageRating,
        completedBooks: userBooks.filter(book => book && book.readStatus === 'completed').length || 0,
        readingBooks: userBooks.filter(book => book && book.readStatus === 'reading').length || 0,
        pinnedBooks: userBooks.filter(book => book && book.isPinned).length || 0
      };

      setRankingData({
        topRatedBooks,
        mostHighlightedBooks,
        favoriteGenres: { topEmotions, topPurposes, topOwnership },
        readingStats
      });
    } catch (error) {
      console.error('랭킹 데이터 계산 오류:', error);
    } finally {
      setRankingLoading(false);
    }
  }, [profileUser, userBooks, bookHighlightCounts, highlightsCount]);

  // 랭킹 뷰 데이터 로딩 (calculateRankingData 함수 선언 이후에 배치)
  useEffect(() => {
    if (currentView === 'ranking' && !dataLoading && profileUser) {
      calculateRankingData();
    }
  }, [currentView, dataLoading, profileUser, calculateRankingData]);

  // Home에서 통계 버튼을 통해 Ranking 뷰로 이동하는 경우 처리
  useEffect(() => {
    if (location.state?.showRanking && currentUser && !dataLoading) {
      // Ranking 뷰로 전환
      setCurrentView('ranking');
      
      // 데이터 로딩이 완료된 후 Ranking 섹션으로 스크롤
      const scrollToRanking = () => {
        if (rankingSectionRef.current) {
          rankingSectionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      };

      // 약간의 지연을 두어 뷰 전환이 완료된 후 스크롤
      const timer = setTimeout(scrollToRanking, 100);
      
      return () => clearTimeout(timer);
    }
  }, [location.state?.showRanking, currentUser, dataLoading]);

  // Home에서 책맞추기 버튼을 통해 Game 뷰로 이동하는 경우 처리
  useEffect(() => {
    if (location.state?.showGame && currentUser && !dataLoading) {
      // Game 뷰로 전환
      setCurrentView('tests');
      
      // 데이터 로딩이 완료된 후 Game 섹션으로 스크롤
      const scrollToGame = () => {
        if (gameSectionRef.current) {
          gameSectionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      };

      // 약간의 지연을 두어 뷰 전환이 완료된 후 스크롤
      const timer = setTimeout(scrollToGame, 100);
      
      return () => clearTimeout(timer);
    }
  }, [location.state?.showGame, currentUser, dataLoading]);

  if (pageLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 120px)' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!userExists) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <Typography variant="h6">존재하지 않는 사용자입니다.</Typography>
        <Button onClick={() => navigate('/')}>홈으로 돌아가기</Button>
      </Box>
    );
  }
  
  if (!currentUser && !paramUserId) {
    return (
      <Box sx={{ 
        maxWidth: "600px", 
        width: "100%",
        margin: "0 auto",
        minHeight: "100vh",
        height: "100vh",
        position: "relative",
        overflow: "hidden"
      }}>
        {/* 배경 이미지와 오버레이 */}
        <Box
          component="img"
          src={`/images/${randomBackgroundImage}`}
          alt="SnapLog 배경 이미지"
          loading="lazy"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            filter: 'blur(8px) brightness(0.7)',
            zIndex: 0,
          }}
        />
        
        {/* 그라데이션 오버레이 */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.6) 100%)',
            zIndex: 1,
          }}
        />

        {/* 메인 콘텐츠 */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            height: '100vh',
            px: 3,
            py: 4,
          }}
        >
          {/* 로고 영역 */}
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Box component="span" sx={{
              fontFamily: 'NanumSquareB',
              fontSize: { xs: '2.5rem', sm: '3rem' },
              fontWeight: 600,
              letterSpacing: '-1px',
              color: 'white',
              textShadow: '0 4px 12px rgba(0,0,0,0.4)',
              display: 'block',
              mb: 1,
            }}>
              Snap
              <Box component="span" sx={{
                fontWeight: 700,
                color: '#FFD700',
                ml: '4px',
                fontFamily: 'NanumSquareB',
                letterSpacing: '-1px',
              }}>
                Log
              </Box>
            </Box>
            <Typography
              sx={{
                fontFamily: 'NanumSquareR',
                fontSize: { xs: '1.1rem', sm: '1.3rem' },
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: 400,
                textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                letterSpacing: '0.5px',
              }}
            >
              책 속 한 문장 기록
            </Typography>
          </Box>

          {/* 메인 메시지 */}
          <Box sx={{ mb: 8, textAlign: 'center', maxWidth: '400px' }}>
            <Typography
              sx={{
                fontFamily: 'NanumSquareEB',
                fontSize: { xs: '1.8rem', sm: '2.2rem' },
                fontWeight: 700,
                color: 'white',
                textShadow: '0 4px 12px rgba(0,0,0,0.5)',
                lineHeight: 1.3,
                mb: 2,
              }}
            >
              독서 기록을
              <br />
              가장 편하게 남겨보세요
            </Typography>
            <Typography
              sx={{
                fontFamily: 'NanumSquareR',
                fontSize: { xs: '1rem', sm: '1.1rem' },
                color: 'rgba(255, 255, 255, 0.85)',
                fontWeight: 400,
                lineHeight: 1.6,
                textShadow: '0 2px 8px rgba(0,0,0,0.3)',
              }}
            >
              마음에 남는 문장을 기록하고,<br />
              나만의 독서 공간을 만들어보세요
            </Typography>
          </Box>

          {/* 로그인 버튼 */}
          <Box sx={{ mb: 4 }}>
            <Button
              variant="contained"
              onClick={handleLogin}
                          startIcon={
              <Box
                component="img"
                src="data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 48 48'%3e%3cpath fill='%23EA4335' d='M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z'/%3e%3cpath fill='%234285F4' d='M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z'/%3e%3cpath fill='%23FBBC05' d='M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z'/%3e%3cpath fill='%2334A853' d='M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z'/%3e%3cpath fill='none' d='M0 0h48v48H0z'/%3e%3c/svg%3e"
                alt="Google 로그인 아이콘"
                loading="lazy"
                sx={{ width: 20, height: 20 }}
              />
            }
              sx={{
                bgcolor: 'white',
                color: '#333',
                fontWeight: 600,
                fontSize: { xs: '1rem', sm: '1.1rem' },
                px: { xs: 4, sm: 5 },
                py: { xs: 1.5, sm: 2 },
                borderRadius: '50px',
                boxShadow: '0 8px 32px rgba(255,255,255,0.3), 0 4px 16px rgba(0,0,0,0.2)',
                textTransform: 'none',
                fontFamily: 'NanumSquareB',
                letterSpacing: '0.5px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  bgcolor: '#f8f9fa',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(255,255,255,0.4), 0 6px 20px rgba(0,0,0,0.3)',
                },
                '&:active': {
                  transform: 'translateY(0px)',
                }
              }}
            >
              구글 계정으로 시작하기
            </Button>
          </Box>

          {/* 하단 설명 */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                fontFamily: 'NanumSquareR',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: 400,
                textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                lineHeight: 1.5,
              }}
            >
              간편한 구글 로그인으로<br />
              바로 시작할 수 있어요
            </Typography>
          </Box>
        </Box>
        
        <InAppBrowserModal 
          open={inAppBrowserModalOpen} 
          onClose={() => setInAppBrowserModalOpen(false)} 
        />
      </Box>
    );
  }
  
  if (!profileUser) {
     console.error("Error: userExists is true, but profileUser is null.");
     return (
       <Box sx={{ textAlign: 'center', p: 4 }}>
         <Typography color="error">페이지 로딩 중 오류가 발생했습니다.</Typography>
         <Button onClick={() => navigate('/')}>홈으로 돌아가기</Button>
       </Box>
     );
  }

  return (
      <Box sx={{ 
        maxWidth: "600px", 
        width: "100%",
        margin: "0 auto",
        paddingBottom: "80px"
      }}
      >
        <Box sx={{ 
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          mb: 3,
          justifyContent: 'flex-start',
          px: 2,
          paddingTop: '24px'
        }}>
          {/* <Avatar 
            src={profileUser.photoURL}
            sx={{ 
              width: 60, 
              height: 60, 
              border: '2px solid rgba(0, 0, 0, 0.1)'
            }} 
          /> 프로필 이미지 주석 처리함*/}
          <Box sx={{ width: '100%'}}>
            <Box sx={{ 
              width: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between' 
            }}>
              <Typography variant="h6" sx={{ fontWeight: '700', fontFamily: 'NanumsquareBold', fontSize: '1.8rem'}}>
                {profileUser.displayName || '익명'}
              </Typography>
              <IconButton
                aria-label="more"
                onClick={() => setMoreSheetOpen(true)}
                size="small"
              >
                <MoreVertIcon />
              </IconButton>
            </Box>
            <Typography
              sx={{
                fontFamily: 'NanumSquareR',
                color: 'text.secondary',
                fontSize: '1rem',
                mt: 0.2,
                mb: 0.2,
                width: '100%',
                maxWidth: '100%',
                lineHeight: 1.7,
                minWidth: 0,
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'normal',
                minHeight: '22px',
                fontStyle: 'normal',
              }}
            >
              {bio ? bio : (isOwner ? '소개메세지를 입력해보세요.' : '')}
            </Typography>
            {!isOwner && currentUser && (
              <Button 
                variant={isFollowing ? "outlined" : "contained"} 
                size="small"
                onClick={handleFollowToggle}
                disabled={followLoading}
                sx={{
                  mt: 0.5, px: 1.5,
                  ...( !isFollowing && {
                      bgcolor: 'grey.900',
                      color: 'white',
                      '&:hover': { bgcolor: 'grey.800' },
                      '&.Mui-disabled': { bgcolor: 'grey.500', color: 'rgba(255, 255, 255, 0.5)' }
                  }),
                }}
              >
                {followLoading ? 
                  <CircularProgress size={20} color="inherit" /> : 
                  (isFollowing ? '팔로잉' : '팔로우')
                }
              </Button>
            )}
          </Box>
        </Box>

        {/* 게임 만들기 배너
        {showGameBanner && (
          <Box sx={{ mb: 3, px: 2 }}>
            <Paper
              elevation={0}
              sx={{
                background: gameBannerDisabled 
                  ? 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)'
                  : 'linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)',
                borderRadius: '16px',
                p: 3,
                position: 'relative',
                overflow: 'hidden',
                cursor: gameBannerDisabled ? 'default' : 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': gameBannerDisabled ? {} : {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 32px rgba(26, 35, 126, 0.3)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'url(/images/home-01.png)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  opacity: 0.1,
                  zIndex: 0,
                }
              }}
              onClick={handleCreateGame}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      background: gameBannerDisabled 
                        ? 'rgba(0,0,0,0.1)' 
                        : 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: '24px',
                        color: gameBannerDisabled ? '#999' : '#FFD700',
                        fontWeight: 'bold',
                      }}
                      role="img"
                      aria-label="게임 아이콘"
                    >
                      🎮
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        color: gameBannerDisabled ? '#666' : 'white',
                        mb: 0.5,
                        fontFamily: 'NanumSquareEB',
                      }}
                    >
                      책 맞추기 게임 어떠세요?
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.9rem',
                        color: gameBannerDisabled ? '#999' : 'rgba(255,255,255,0.9)',
                        fontFamily: 'NanumSquareR',
                      }}
                    >
                      {gameBannerDisabled 
                        ? `문장 ${Math.max(5 - highlightsCount, 0)}개, 책 ${Math.max(3 - userBooks.length, 0)}권 더 등록하면 나만의 퀴즈를 만들 수 있어요!`
                        : '나만의 하이라이트 퀴즈를 만들어보세요!'
                      }
                    </Typography>
                  </Box>
                </Box>
                
                {!gameBannerDisabled && (
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      bgcolor: '#FFD700',
                      color: '#1a237e',
                      fontWeight: 700,
                      borderRadius: '20px',
                      px: 3,
                      py: 1,
                      textTransform: 'none',
                      fontFamily: 'NanumSquareEB',
                      '&:hover': {
                        bgcolor: '#FFC107',
                        transform: 'scale(1.05)',
                      },
                    }}
                  >
                    게임 만들기
                  </Button>
                )}
              </Box>
            </Paper>
          </Box>
        )} */}

        {/* 업적 시스템 스타일 UI - 그리드 톤앤매너 통일 */}
        <Box sx={{ mb: 2, px: 2 }}>
          {/* 저장한 책 업적 */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              bgcolor: '#fff',
              borderRadius: 1,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              p: 1.5,
              mb: 1.5,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              border: '1px solid rgba(0,0,0,0.04)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transform: 'translateY(-2px)',
              }
            }} 
            onClick={() => setCurrentView('list')}
          >
            {/* 좌측 수치 표시 영역 */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              mr: 2,
              minWidth: 44,
              height: 44,
              borderRadius: 1,
              bgcolor: 'rgba(255, 193, 7, 0.1)',
            }}>
             <Typography 
                variant="caption" 
                sx={{ fontSize: '10px', fontWeight: 600, color: '#666', lineHeight: 1, mb: 0.25 }}
              >
                저장한 책
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#333', lineHeight: 1 }}>
                {booksCount}
              </Typography>
            </Box>
            
            {/* 업적 정보 */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#333', mb: 0.3, lineHeight: 1.2 }}>
                {booksCount >= 100 ? '[책 지피티] 당신은 책 지피티입니다다' : 
                 booksCount >= 50 ? '[사서급] 도서관 사서급의 전투력' : 
                 booksCount >= 10 ? '[독서가] 책 좀 읽어 본 사람' : '[독서아기] 독서량이 갓 태어난 아기 수준'}
              </Typography>
              {booksCount >= 100 ? (
                <Typography sx={{ fontSize: '11px', fontWeight: 500, color: '#666', mb: 0.75, lineHeight: 1.3 }}>
                  🏆 최고 등급! 이제 진짜 서점 차릴 준비 완료
                </Typography>
              ) : (
                <Typography sx={{ fontSize: '11px', fontWeight: 500, color: '#666', mb: 0.75, lineHeight: 1.3 }}>
                  {booksCount >= 50 ? `앞으로 ${100 - booksCount}권 더 등록하면 [책 지피티]로 진화 ` : 
                   booksCount >= 10 ? `앞으로 ${50 - booksCount}권 더 등록하면 [도서관 사서]로 승급` : 
                   `앞으로 ${10 - booksCount}권 더 등록하면 [독서가] 인증`}
                </Typography>
              )}
              <Box sx={{ width: '100%' }}>
                <LinearProgress 
                  variant="determinate" 
                  value={(() => {
                    if (booksCount >= 100) return 100;
                    if (booksCount >= 50) return 50 + ((booksCount - 50) / 50) * 50;
                    if (booksCount >= 10) return (booksCount - 10) / 40 * 50;
                    return (booksCount / 10) * 25;
                  })()}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    bgcolor: 'rgba(0,0,0,0.06)',
                    '& .MuiLinearProgress-bar': { borderRadius: 2, bgcolor: 'rgba(255, 193, 7, 0.8)' }
                  }}
                />
              </Box>
            </Box>
            
          </Box>



          {/* 동행자 업적 (소유자만 표시) */}
          {isOwner && (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                bgcolor: '#fff',
                borderRadius: 1,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                p: 1.5,
                mb: 1.5,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                border: '1px solid rgba(0,0,0,0.04)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  transform: 'translateY(-2px)',
                }
              }} 
              onClick={handleOpenFollowingList}
            >
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                mr: 2,
                minWidth: 44,
                height: 44,
                borderRadius: 1,
                bgcolor: 'rgba(156, 39, 176, 0.08)',
              }}>
                <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#666', lineHeight: 1, mb: 0.25 }}>
                  동행자
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#333', lineHeight: 1 }}>
                  {followingCount} 명
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#333', mb: 0.3, lineHeight: 1.2 }}>
                  {followingCount >= 100 ? '[기세등등] 인맥왕' : 
                   followingCount >= 50 ? '[친구많음] 기세등등합니다' : 
                   followingCount >= 10 ? '[친구있음] 친구 좀 있는 편' : followingCount >= 1 ? '[친구없음] 읽은 책보다 아는 사람이 더 적음' : '[고독함] 친구 없음'}
                </Typography>
                {followingCount >= 100 ? (
                  <Typography sx={{ fontSize: '11px', fontWeight: 500, color: '#666', mb: 0.75, lineHeight: 1.3 }}>
                    🏆 최고 등급! 이제 진짜 인맥왕 인증 완료
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: '11px', fontWeight: 500, color: '#666', mb: 0.75, lineHeight: 1.3 }}>
                    {followingCount >= 50 ? `앞으로 ${100 - followingCount}명 더 팔로우하면 [인맥왕]로 진화` : 
                     followingCount >= 10 ? `앞으로 ${50 - followingCount}명 더 팔로우하면 [친구많음]로 승급` : 
                     followingCount >= 1 ? `앞으로 ${10 - followingCount}명 더 팔로우하면 [친구있음] 인증` : 
                     `앞으로 ${1 - followingCount}명 더 팔로우하면 [고독탈출] 인증`}
                  </Typography>
                )}
                <Box sx={{ width: '100%' }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={(() => {
                      if (followingCount >= 100) return 100;
                      if (followingCount >= 50) return 50 + ((followingCount - 50) / 50) * 50;
                      if (followingCount >= 10) return (followingCount - 10) / 40 * 50;
                      return (followingCount / 10) * 25;
                    })()}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      bgcolor: 'rgba(0,0,0,0.06)',
                      '& .MuiLinearProgress-bar': { borderRadius: 2, bgcolor: 'rgba(156, 39, 176, 0.7)' }
                    }}
                  />
                </Box>
              </Box>
              
            </Box>
          )}

          {/* 팔로워 업적 */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              bgcolor: '#fff',
              borderRadius: 1,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              p: 1.5,
              mb: 1.5,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              border: '1px solid rgba(0,0,0,0.04)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transform: 'translateY(-2px)',
              }
            }} 
            onClick={() => setIsFollowerDialogOpen(true)}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              mr: 2,
              minWidth: 44,
              height: 44,
              borderRadius: 1,
              bgcolor: 'rgba(255, 152, 0, 0.08)',
            }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#666', lineHeight: 1, mb: 0.25 }}>
                추종자
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#333', lineHeight: 1 }}>
                {followerCount}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#333', mb: 0.3, lineHeight: 1.2 }}>
                {followerCount >= 1000 ? '[인맥왕] 읽은 책보다 친구가 더 많음' : 
                 followerCount >= 500 ? '[친구많음] 인맥왕' : 
                 followerCount >= 100 ? '[기세등등] 친구 많음' : followerCount >= 10 ? '[친구 있음] 많진 않음' : '친구 없음'}
              </Typography>
              {followerCount >= 1000 ? (
                <Typography sx={{ fontSize: '11px', fontWeight: 500, color: '#666', mb: 0.75, lineHeight: 1.3 }}>
                  🏆 최고 등급! 이제 진짜 인맥왕 인증 완료
                </Typography>
              ) : (
                <Typography sx={{ fontSize: '11px', fontWeight: 500, color: '#666', mb: 0.75, lineHeight: 1.3 }}>
                  {followerCount >= 500 ? `앞으로 ${1000 - followerCount}명 더 팔로워를 모으면 [인맥왕]으로 진화` : 
                   followerCount >= 100 ? `앞으로 ${500 - followerCount}명 더 팔로워를 모으면 [친구많음] 인증` : 
                   followerCount >= 10 ? `앞으로 ${100 - followerCount}명 더 팔로워를 모으면 [존재 증명] 가능` : 
                   `앞으로 추종자 ${10 - followerCount}명 더 모으면 왕따 탈출`}
                </Typography>
              )}
              <Box sx={{ width: '100%' }}>
                <LinearProgress 
                  variant="determinate" 
                  value={(() => {
                    if (followerCount >= 1000) return 100;
                    if (followerCount >= 500) return 50 + ((followerCount - 500) / 500) * 50;
                    if (followerCount >= 100) return (followerCount - 100) / 400 * 50;
                    return (followerCount / 100) * 25;
                  })()}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    bgcolor: 'rgba(0,0,0,0.06)',
                    '& .MuiLinearProgress-bar': { borderRadius: 2, bgcolor: 'rgba(255, 152, 0, 0.7)' }
                  }}
                />
              </Box>
            </Box>
            
          </Box>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          width: '100%', 
          borderBottom: 1, 
          borderColor: 'divider',
          mb: 0,
          px: 0,
          py: 0,
          width: '100%',
        }}>
          <IconButton 
            onClick={() => setCurrentView('grid')} 
            sx={{
              flex: 1, borderRadius: 0, py: 1.5, 
              color: currentView === 'grid' ? 'text.primary' : 'text.secondary', 
              borderBottom: currentView === 'grid' ? 2 : 0, 
              borderColor: 'text.primary' 
            }}
          >
            <GridOnIcon />
          </IconButton>
          <IconButton 
            onClick={() => setCurrentView('list')} 
            sx={{
              flex: 1, borderRadius: 0, py: 1.5, 
              color: currentView === 'list' ? 'text.primary' : 'text.secondary', 
              borderBottom: currentView === 'list' ? 2 : 0, 
              borderColor: 'text.primary' 
            }}
          >
            <ViewListIcon />
          </IconButton>
          <IconButton 
            onClick={() => setCurrentView('highlights')} 
            sx={{
              flex: 1, borderRadius: 0, py: 1.5, 
              color: currentView === 'highlights' ? 'text.primary' : 'text.secondary', 
              borderBottom: currentView === 'highlights' ? 2 : 0, 
              borderColor: 'text.primary' 
            }}
          >
            <BookmarkBorderIcon /> 
          </IconButton>
          {/* Ranking View 탭 추가 */}
          <IconButton 
            onClick={() => setCurrentView('ranking')} 
            sx={{
              flex: 1, borderRadius: 0, py: 1.5, 
              color: currentView === 'ranking' ? 'text.primary' : 'text.secondary', 
              borderBottom: currentView === 'ranking' ? 2 : 0, 
              borderColor: 'text.primary' 
            }}
          >
            <EmojiEventsIcon />
          </IconButton>
          {/* Test View 탭 추가 */}
          {isOwner && (
            <IconButton 
              onClick={() => setCurrentView('tests')} 
              sx={{
                flex: 1, borderRadius: 0, py: 1.5, 
                color: currentView === 'tests' ? 'text.primary' : 'text.secondary', 
                borderBottom: currentView === 'tests' ? 2 : 0, 
                borderColor: 'text.primary' 
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                }}
                role="img"
                aria-label="게임 아이콘"
              >
                🎮
              </Box>
            </IconButton>
          )}
        </Box>

        <Box 
          sx={{ 
            px: currentView === 'list' ? 1 : 0,
            backgroundColor: currentView === 'list' ? '#f5f7fa' : 'transparent',
            minHeight: currentView === 'list' ? '100vh' : 'auto'
          }}
          // height, overflowY, className, onScroll 모두 제거
        >
          {dataLoading && (
             <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
               <CircularProgress />
             </Box>
          )}

          {!dataLoading && currentView === 'grid' && (
             <Box sx={{ 
               display: 'grid',
               gridTemplateColumns: {
                 xs: 'repeat(2, 1fr)',
                 sm: 'repeat(3, 1fr)',
                 md: 'repeat(4, 1fr)',
                 lg: 'repeat(5, 1fr)',
               },
               gap: { xs: 1, sm: 1.5, md: 2 },
               pl: { xs: 1, sm: 1.5, md: 2 },
               pr: { xs: 1, sm: 1.5, md: 2 },
               pb: 2,
               width: '100%',
               maxWidth: 1200,
               mx: 'auto',
             }}>
               {displayedBooks.length === 0 && !loadingBooks ? (
                 <Typography sx={{textAlign: 'center', py: 4, gridColumn: '1 / -1', color: 'text.secondary', fontSize: '0.9rem'}}>등록된 책이 없습니다.</Typography>
               ) : (
                 <>
                   {displayedBooks.map((book) => (
                     <Box 
                       key={book.id}
                       component="article"
                       role="button"
                       tabIndex={0}
                       onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/book/${book.id}?uid=${profileUser.uid}`); } }}
                       aria-label={`${book.title}${book.authors ? ` - ${book.authors.join(', ')}` : ''}, ${book.readStatus === 'completed' ? '완독' : book.readStatus === 'reading' ? '읽는 중' : '읽기 전'}`}
                       sx={{ 
                         position: 'relative', 
                         cursor: 'pointer', 
                         display: 'flex',
                         flexDirection: 'column',
                         borderRadius: 1,
                         overflow: 'hidden',
                         backgroundColor: '#fff',
                         boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                         transition: 'all 0.2s ease',
                         minHeight: 44,
                         '&:hover': { 
                           boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                           transform: 'translateY(-2px)',
                         },
                         '&:focus-visible': {
                           outline: '2px solid #333',
                           outlineOffset: 2,
                         },
                       }}
                       onClick={() => navigate(`/book/${book.id}?uid=${profileUser.uid}`)}
                     >
                       {/* (1) Cover 영역 - aspect-ratio 고정, 썸네일 중앙 정렬 */}
                       <Box
                         sx={{
                           position: 'relative',
                           aspectRatio: '141 / 209',
                           width: '100%',
                           backgroundColor: '#f0f0f0',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           overflow: 'hidden',
                         }}
                       >
                         {/* 상단 좌측: 핀 아이콘 */}
                         {book.isPinned && (
                           <Box
                             sx={{
                               position: 'absolute',
                               top: 6,
                               left: 6,
                               zIndex: 4,
                               pointerEvents: 'none',
                               display: 'flex',
                               alignItems: 'center',
                               justifyContent: 'center',
                               width: 22,
                               height: 22,
                               borderRadius: '50%',
                               background: 'rgba(0,0,0,0.5)',
                               backdropFilter: 'blur(4px)',
                             }}
                           >
                             <PushPinIcon sx={{ fontSize: '12px', color: '#fff', transform: 'rotate(-45deg)' }} />
                           </Box>
                         )}
                         {/* 상단 좌측: 상태 Badge (읽는 중/읽기 전/완독) */}
                         {book.readStatus && (
                           <Box
                             sx={{
                               position: 'absolute',
                               top: 6,
                               right: 6,
                               zIndex: 4,
                               pointerEvents: 'none',
                               px: 1,
                               py: 0.25,
                               borderRadius: 1,
                               fontSize: '10px',
                               fontWeight: 600,
                               backgroundColor: book.readStatus === 'completed' ? 'rgba(76, 175, 80, 0.9)' :
                                                book.readStatus === 'reading' ? 'rgba(33, 150, 243, 0.9)' :
                                                'rgba(97, 97, 97, 0.9)',
                               color: '#fff',
                               boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                             }}
                           >
                             {book.readStatus === 'completed' ? '완독' : book.readStatus === 'reading' ? '읽는 중' : '읽기 전'}
                           </Box>
                         )}
                         {/* 썸네일 - 중앙 정렬 (이미지 비율 관계없이 레이아웃 유지) */}
                         {!book.userThumbnail ? (
                           <>
                             <img
                               src={book.thumbnail || '/images/no-cover.png'}
                               alt={`${book.title}${book.authors ? ` - ${book.authors.join(', ')}` : ''}`}
                               loading="lazy"
                               onError={(e) => { e.target.onerror = null; e.target.src = '/images/no-cover.png'; }}
                               style={{ 
                                 maxWidth: '85%',
                                 maxHeight: '95%',
                                 width: 'auto',
                                 height: 'auto',
                                 objectFit: 'contain',
                                 display: 'block',
                                 boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                                 borderRadius: 2,
                               }}
                             />
                           </>
                         ) : (
                           <img
                             src={book.userThumbnail}
                             alt={`${book.title}${book.authors ? ` - ${book.authors.join(', ')}` : ''}`}
                             loading="lazy"
                             onError={(e) => { e.target.onerror = null; e.target.src = '/images/no-cover.png'; }}
                             style={{ 
                               width: '100%',
                               height: '100%',
                               objectFit: 'cover',
                               display: 'block',
                             }}
                           />
                         )}
                       </Box>
                       {/* (2) Meta 영역 - 기록한 글 N개 */}
                       <Box
                         sx={{
                           px: 1,
                           py: 0.75,
                           minHeight: 32,
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           backgroundColor: '#fff',
                         }}
                       >
                         {(optimizedHighlightCounts[book.id] || 0) > 0 ? (
                           <Typography
                             component="span"
                             sx={{
                               fontSize: '11px',
                               fontWeight: 500,
                               color: '#666',
                               lineHeight: 1,
                             }}
                           >
                             기록한 글 {optimizedHighlightCounts[book.id]}개
                           </Typography>
                         ) : (
                           <Typography component="span" sx={{ fontSize: '11px', color: '#999', lineHeight: 1 }}>&nbsp;</Typography>
                         )}
                       </Box>
                     </Box>
                   ))}
                   {loadingBooks && (
                     <Box sx={{ 
                       display: 'flex', 
                       justifyContent: 'center', 
                       alignItems: 'center', 
                       gridColumn: '1 / -1', 
                       py: 2 
                     }}>
                       <CircularProgress size={24} />
                     </Box>
                   )}
                 </>
               )}
             </Box>
          )}

          {!dataLoading && currentView === 'list' && (
             <Box sx={{ 
               width: '100%', 
               px: 0, 
               mx: 0, 
               my: 0,
               backgroundColor: 'transparent',
               borderRadius: '0px',
               overflow: 'visible'
             }} >
               {displayedBooks.length === 0 && !loadingBooks ? (
                 <Typography sx={{textAlign: 'center', py: 4, color: 'text.secondary'}}>등록된 책이 없습니다.</Typography>
               ) : (
                 <>
                   {displayedBooks.map((book) => (
                     <Paper
                       key={book.id}
                       elevation={0}
                       onClick={() => navigate(`/book/${book.id}?uid=${profileUser.uid}`)}
                       sx={{
                         cursor: 'pointer',
                         pt: 2,
                         pb: 1.5,
                         px: 2,
                         mx: 0.5,
                         my: 1,
                         backgroundColor: '#ffffff',
                         borderRadius: '8px',
                         borderBottom: 'none',
                         transition: 'all 0.2s ease-in-out',
                         '&:hover': { 
                           backgroundColor: '#ffffff',
                           boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                           transform: 'translateY(-1px)'
                         },
                         '&:last-child': { borderBottom: 'none' },
                         position: 'relative',
                         overflow: 'visible',
                         display: 'block',
                         boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                         border: 'none',
                       }}
                     >
                       {/* 1행: 기존 카드 컨텐츠 (flex row) */}
                       <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                         <Box sx={{ flexShrink: 0, width: 70, height: 96, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                           <img
                             src={book.thumbnail || '/images/no-cover.png'}
                             alt={`${book.title}${book.authors ? ` - ${book.authors.join(', ')}` : ''}`}
                             loading="lazy"
                             onError={(e) => { e.target.onerror = null; e.target.src = '/images/no-cover.png'; }}
                             style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
                           />
                         </Box>
                         <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}>
                           <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                             <Typography 
                               variant="h1"
                               sx={{ 
                                 fontWeight: 'bold',
                                 fontSize: '15px',
                                 color: '#333',
                                 mb: 1,
                                 display: '-webkit-box',
                                 WebkitLineClamp: 2,
                                 WebkitBoxOrient: 'vertical',
                                 overflow: 'hidden',
                                 textOverflow: 'ellipsis',
                                 wordBreak: 'break-word',
                                 flex: 1,
                               }} 
                             >
                               {book.title}
                             </Typography>
                             {book.isPinned && (
                               <PushPinIcon 
                                 sx={{ 
                                   fontSize: '16px', 
                                   color: '#1a237e',
                                   mt: 0.5,
                                   flexShrink: 0,
                                 }} 
                               />
                             )}
                           </Box>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '12px', color: '#888', mt: 0.5 }}>
                             <Typography variant="caption" sx={{ color: 'inherit' }}>
                               {book.createdAt?.toDate().toLocaleDateString() || '등록일 정보 없음'}
                             </Typography>
                             <>
                               <Box component="span" sx={{ fontSize: '10px', color: '#aaa' }}>•</Box>
                               <Typography variant="caption" sx={{ color: 'inherit' }}>
                                 문장 {bookHighlightCounts[book.id] || 0}개
                               </Typography>
                             </>
                           </Box>
                           {/* 태그 영역: 텍스트 기반 트렌디한 디자인 */}
                           {(book.readStatus || book.starRating || book.recommendation || book.mustRead || (Array.isArray(book.emotions) && book.emotions.length > 0) || (Array.isArray(book.genres) && book.genres.length > 0)) && (
                             <Box sx={{ 
                               display: 'flex', 
                               flexWrap: 'wrap', 
                               alignItems: 'center', 
                               gap: 1.2, 
                               mt: 1.5, 
                               mb: 0.5,
                               fontSize: '13px',
                               lineHeight: 1.2
                             }}>
                               {/* 상태 텍스트 */}
                               {book.readStatus && (
                                 <Typography
                                   component="span"
                                   sx={{
                                     fontWeight: 700,
                                     color: book.readStatus === 'completed' ? '#000' :
                                            book.readStatus === 'reading' ? '#4facfe' : '#888',
                                     fontSize: 'inherit',
                                     lineHeight: 'inherit',
                                     letterSpacing: '-0.02em',
                                   }}
                                 >
                                   {book.readStatus === 'completed'
                                     ? '완독'
                                     : book.readStatus === 'reading'
                                       ? '읽는 중'
                                       : '읽기 전'}
                                 </Typography>
                               )}
                                                               {/* 별점 텍스트 */}
                                {book.starRating && (
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontWeight: 700,
                                      color: '#000',
                                      fontSize: 'inherit',
                                      lineHeight: 'inherit',
                                      letterSpacing: '-0.02em',
                                    }}
                                  >
                                    {'★'.repeat(book.starRating)}
                                  </Typography>
                                )}
                               {/* 추천 텍스트 */}
                               {book.recommendation && (
                                 <Typography
                                   component="span"
                                   sx={{
                                     fontWeight: 700,
                                     color: book.recommendation === '추천' ? '#ff6b6b' :
                                            book.recommendation === '보통' ? '#4ecdc4' :
                                            book.recommendation === '비추천' ? '#95a5a6' : '#000',
                                     fontSize: 'inherit',
                                     lineHeight: 'inherit',
                                     letterSpacing: '-0.02em',
                                   }}
                                 >
                                   {book.recommendation === '추천' ? '💖추천' : 
                                    book.recommendation === '보통' ? '🤷‍♀️보통' : 
                                    book.recommendation === '비추천' ? '🙅비추천' : 
                                    book.recommendation}
                                 </Typography>
                               )}
                               {/* 필독 텍스트 */}
                               {book.mustRead && ['필독', '참고용', '미루기'].includes(book.mustRead) && (
                                 <Typography
                                   component="span"
                                   sx={{
                                     fontWeight: 700,
                                     color: book.mustRead === '필독' ? '#e74c3c' :
                                            book.mustRead === '참고용' ? '#f39c12' :
                                            book.mustRead === '미루기' ? '#9b59b6' : '#000',
                                     fontSize: 'inherit',
                                     lineHeight: 'inherit',
                                     letterSpacing: '-0.02em',
                                   }}
                                 >
                                   {book.mustRead === '필독' ? '📌필독' : 
                                    book.mustRead === '참고용' ? '🗒️참고용' : 
                                    book.mustRead === '미루기' ? '⏳미루기' : 
                                    book.mustRead}
                                 </Typography>
                               )}
                               {/* 감정 텍스트 */}
                               {Array.isArray(book.emotions) && book.emotions.length > 0 && book.emotions.map((emo, idx) => (
                                 ['따뜻함', '통찰', '슬픔', '지루함', '기대 이하'].includes(emo) ? (
                                   <Typography
                                     key={`emo${idx}`}
                                     component="span"
                                     sx={{
                                       fontWeight: 700,
                                       color: emo === '따뜻함' ? '#27ae60' :
                                              emo === '통찰' ? '#3498db' :
                                              emo === '슬픔' ? '#9b59b6' :
                                              emo === '지루함' ? '#95a5a6' :
                                              emo === '기대 이하' ? '#e67e22' : '#000',
                                       fontSize: 'inherit',
                                       lineHeight: 'inherit',
                                       letterSpacing: '-0.02em',
                                     }}
                                   >
                                     {emo === '따뜻함' ? '🌿따뜻함' : 
                                      emo === '통찰' ? '📘통찰' : 
                                      emo === '슬픔' ? '😢슬픔' : 
                                      emo === '지루함' ? '😴지루함' : 
                                      emo === '기대 이하' ? '❗기대 이하' : 
                                      emo}
                                   </Typography>
                                 ) : null
                               ))}
                               {/* 장르 텍스트 */}
                               {Array.isArray(book.genres) && book.genres.length > 0 && book.genres.map((genre, idx) => (
                                 ['에세이','자기계발','소설','심리학','인문','경제','역사','기타'].includes(genre) ? (
                                   <Typography
                                     key={`genre${idx}`}
                                     component="span"
                                     sx={{
                                       fontWeight: 700,
                                       color: '#666',
                                       fontSize: 'inherit',
                                       lineHeight: 'inherit',
                                       letterSpacing: '-0.02em',
                                     }}
                                   >
                                     {genre}
                                   </Typography>
                                 ) : null
                               ))}
                             </Box>
                           )}
                         </Box>
                       </Box>
                       {/* 2행: 리뷰 텍스트 (카드 하단 전체) */}
                       {book.memo && (
                         <Box
                           sx={{
                             width: '100%',
                             pt: 1,
                             color: '#2c3e50',
                             fontSize: '0.9rem',
                             fontFamily: "'NanumSquareR', 'Noto Sans KR', sans-serif",
                             lineHeight: 1.8,
                             fontWeight: 400,
                             letterSpacing: '-0.01em',
                             whiteSpace: 'pre-wrap',
                             overflow: 'visible',
                             textOverflow: 'unset',
                             display: 'block',
                             background: 'transparent',
                             borderRadius: '0px',
                             padding: '0px',
                             marginTop: '8px',
                             border: 'none',
                             minHeight: 'auto',
                             maxWidth: '100%',
                             wordBreak: 'break-word',
                           }}
                         >
                           {book.memo}
                         </Box>
                       )}
                     </Paper>
                   ))}
                   {loadingBooks && (
                     <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                       <CircularProgress size={24} />
                     </Box>
                   )}
                 </>
               )}
               
             </Box>
          )}
          
          {!dataLoading && currentView === 'highlights' && (
             <>
               {displayedHighlights.length === 0 && !loadingDisplayedHighlights ? (
                 <Typography sx={{ textAlign: 'center', py: 4, color: 'text.secondary', fontFamily: 'Noto Sans, Pretendard, sans-serif' }}>
                   작성한 문장이 없습니다.
                 </Typography>
               ) : (
                 <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 1, width: '100%', borderRadius: 0 }}>
                   {displayedHighlights.map((highlight) => {
                     // 썸네일 이미지 경로
                     const thumbnail = highlight.bookThumbnail || '/images/no-cover.png';
                     // 작성자명
                     const displayName = isOwner ? undefined : (highlight.userName || '익명');
                     // 이동용 bookDocId 생성
                     let bookDocId = highlight.bookDocId;
                     if (!bookDocId && highlight.bookId && profileUser?.uid) {
                       bookDocId = `${profileUser.uid}_${highlight.bookId}`;
                     }
                     return (
                       <Paper
                         key={highlight.id}
                         elevation={0}
                         onClick={() => {
                           if (bookDocId) {
                             navigate(`/book/${bookDocId}?uid=${profileUser.uid}&highlightId=${highlight.id}`);
                           } else {
                             navigate(`/book/${highlight.bookId}?uid=${profileUser.uid}&highlightId=${highlight.id}`);
                           }
                         }}
                         sx={{
                           position: 'relative',
                           width: '100%',
                           maxWidth: 'none',
                           minWidth: 0,
                           bgcolor: '#F6F6F6',
                           boxShadow: '0px 4px 8px rgba(0,0,0,0.10)',
                           overflow: 'hidden',
                           px: 0,
                           mx: 0,
                           my: 0,
                           py: 0,
                           mb: 2,
                           cursor: 'pointer',
                           transition: 'box-shadow 0.18s',
                           '&:hover': {
                             boxShadow: '0px 8px 24px rgba(0,0,0,0.13)',
                           },
                           display: 'block',
                           boxSizing: 'border-box',
                         }}
                       >
                         {/* 문장 텍스트 */}
                         <Box
                           sx={{
                             width: { xs: '100%', sm: '100%' },
                             pt: { xs: '20px', sm: '28px' },
                             pl: { xs: '16px', sm: '24px' },
                             pr: { xs: '16px', sm: '24px' },
                             boxSizing: 'border-box',
                             zIndex: 2,
                             fontFamily: 'NanumSquareR',
                             fontWeight: 500,
                             fontSize: { xs: 15, sm: 17, md: 18 },
                             letterSpacing: '0.02em',
                             lineHeight: { xs: 1.7, sm: 1.7, md: 1.7 },
                             color: '#000000',
                             overflow: 'hidden',
                             textOverflow: 'ellipsis',
                             display: '-webkit-box',
                             WebkitBoxOrient: 'vertical',
                             whiteSpace: 'pre-wrap',
                             wordBreak: 'break-word',
                             background: 'transparent',
                             minHeight: 0,
                           }}
                         >
                           {highlight.text}
                         </Box>
                         {/* 하단 정보 그룹 */}
                         <Box
                           sx={{
                             position: 'relative', // absolute -> relative로 변경
                             left: 'auto',
                             right: 'auto',
                             bottom: 'auto',
                             left: { xs: 16, sm: 24 },
                             right: { xs: 90, sm: 90 }, // 썸네일+여백만큼 오른쪽 공간 확보
                             bottom: 0, // 썸네일 right와 동일하게
                             height: 59.3, // 썸네일과 동일
                             mt: 2.5,
                             display: 'flex',
                             flexDirection: 'column',
                             alignItems: 'flex-start',
                             gap: '2px', // 미세 조정
                             zIndex: 2,
                             bgcolor: 'transparent', // 100% 투명 배경
                           }}
                         >
                           <Typography
                             sx={{
                               fontFamily: 'Noto Sans KR',
                               fontWeight: 500,
                               fontSize: 14,
                               lineHeight: '17px',
                               color: '#777',
                               opacity: 0.8,
                               width: '100%',
                               overflow: 'hidden',
                               textOverflow: 'ellipsis',
                               whiteSpace: 'nowrap',
                             }}
                           >
                            {highlight.createdAt && (typeof highlight.createdAt.toDate === 'function'
                               ? highlight.createdAt.toDate().toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\./g, '.').replace(/\s/g, '')
                               : new Date(highlight.createdAt).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\./g, '.').replace(/\s/g, '')
                             )}
                           </Typography>
                           <Typography
                             sx={{
                               fontFamily: 'NanumSquareR',
                               fontWeight: 500,
                               fontSize: 16,
                               lineHeight: '19px',
                               color: '#444444',
                               opacity: 0.9,
                               width: '70%', // 항상 가용 공간 전체 사용
                               overflow: 'hidden',
                               textOverflow: 'ellipsis',
                               whiteSpace: 'nowrap', // 1줄만 보이고 초과시 ...
                             }}
                           >
                             {highlight.bookTitle}
                           </Typography>
                         </Box>
                         {/* 썸네일 이미지 (카드 하단에 항상 붙도록) */}
                         <Box
                           sx={{
                             position: 'absolute',
                             right: 16,
                             bottom: 0, // 하단에 딱 붙게
                             width: 58.83,
                             height: 59.3,
                             borderRadius: '4px 4px 0 0',
                             boxShadow: '4px 8px 8px 0px rgba(0,0,0,0.36)',
                             overflow: 'hidden',
                             zIndex: 3,
                             bgcolor: '#eee',
                             display: 'flex',
                             alignItems: 'flex-start',
                             justifyContent: 'center',
                           }}
                         >
                           <Box
                             component="img"
                             src={thumbnail}
                             alt={`${highlight.bookTitle}${highlight.bookAuthors && highlight.bookAuthors.length > 0 ? ` - ${highlight.bookAuthors.join(', ')}` : ''}`}
                             loading="lazy"
                             sx={{
                               width: '100%',
                               height: '135%',
                               objectFit: 'cover',
                               objectPosition: 'top',
                               display: 'block',
                               background: '#eee',
                             }}
                           />
                         </Box>
                         {/* 카드 우상단 액션(수정/삭제) */}
                         {/* {isOwner && (
                           <Box
                             sx={{
                               position: 'absolute',
                               top: 8,
                               right: 8,
                               zIndex: 4,
                             }}
                           >
                             <IconButton
                               aria-label="highlight-actions"
                               aria-controls={`highlight-menu-${highlight.id}`}
                               aria-haspopup="true"
                               onClick={(e) => { e.stopPropagation(); handleOpenHighlightMenu(e, highlight); }}
                               sx={{ bgcolor: 'rgba(245,245,245,0.7)', '&:hover': { bgcolor: 'rgba(230,230,230,0.9)' }, boxShadow: 'none', p: 1 }}
                               size="small"
                             >
                               <MoreVertIcon />
                             </IconButton>
                           </Box>
                         )} */}
                       </Paper>
                     );
                   })}
                   {loadingDisplayedHighlights && ( <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box> )}
                 </Box>
               )}
             </>
          )}

          {/* Test View */}
          {!dataLoading && currentView === 'tests' && (
            <Box ref={gameSectionRef} sx={{ px: 2 }}>
              {testLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : userTests.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: '32px',
                        color: '#999',
                      }}
                      role="img"
                      aria-label="게임 아이콘"
                    >
                      🎮
                    </Box>
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      mb: 1,
                      fontFamily: 'NanumSquareB',
                    }}
                  >
                    아직 만든 테스트가 없어요
                  </Typography>
                  <Typography
                    sx={{
                      color: 'text.secondary',
                      mb: 3,
                      fontFamily: 'NanumSquareR',
                    }}
                  >
                    첫 번째 문장 테스트를 만들어보세요!
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleCreateGame}
                    sx={{
                      bgcolor: '#1a237e',
                      color: 'white',
                      fontWeight: 700,
                      borderRadius: '20px',
                      px: 4,
                      py: 1.5,
                      textTransform: 'none',
                      fontFamily: 'NanumSquareB',
                      '&:hover': {
                        bgcolor: '#283593',
                      },
                    }}
                  >
                    테스트 만들기
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mt: 2,
                      mb: 1,
                      fontFamily: 'NanumSquareEB',
                    }}
                  >
                  {userTests.length}개의 테스트
                  </Typography>
                  {userTests.map((test) => (
                    <Paper
                      key={test.id}
                      elevation={0}
                      sx={{
                        mb: 2,
                        p: 3,
                        borderRadius: '16px',
                        border: '1px solid #e0e0e0',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              fontSize: '20px',
                              color: '#FFD700',
                            }}
                            role="img"
                            aria-label="타겟 아이콘"
                          >
                            🎯
                          </Box>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: '1.1rem',
                              mb: 0.5,
                              fontFamily: 'NanumSquareB',
                              wordBreak: 'break-word',
                              lineHeight: 1.3,
                            }}
                          >
                            {generateKitchyTitle(test.title, test.creatorName, test.id)}
                          </Typography>
                          <Typography
                            sx={{
                              color: 'text.secondary',
                              fontSize: '0.9rem',
                              fontFamily: 'NanumSquareR',
                            }}
                          >
                            {test.questionCount}문제 • {test.participantCount || 0}명 참여
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1,
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1,
                          flexWrap: 'wrap',
                          flex: 1,
                          minWidth: 0,
                        }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handlePlayTest(test.id)}
                            sx={{
                              borderColor: '#1a237e',
                              color: '#1a237e',
                              fontWeight: 600,
                              borderRadius: '20px',
                              px: 2,
                              textTransform: 'none',
                              fontFamily: 'NanumSquareB',
                              '&:hover': {
                                borderColor: '#283593',
                                bgcolor: 'rgba(26, 35, 126, 0.04)',
                              },
                            }}
                          >
                            테스트하기
                          </Button>
                          <Tooltip 
                            title={isMobileDevice() ? "공유하기" : "링크 복사하기"}
                            placement="top"
                            arrow
                          >
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleShareTest(test.id)}
                              sx={{
                                bgcolor: '#FFD700',
                                color: '#1a237e',
                                fontWeight: 600,
                                borderRadius: '20px',
                                px: 2,
                                textTransform: 'none',
                                fontFamily: 'NanumSquareB',
                                '&:hover': {
                                  bgcolor: '#FFC107',
                                },
                              }}
                            >
                              공유하기
                            </Button>
                          </Tooltip>
                        </Box>
                        
                        {/* 수정/삭제 버튼 - 오른쪽에 배치 */}
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 0.5, 
                          ml: { xs: 0, sm: 2 },
                          flexShrink: 0,
                        }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditTestTitle(test);
                            }}
                            sx={{
                              color: '#666',
                              '&:hover': {
                                color: '#1a237e',
                                bgcolor: 'rgba(26, 35, 126, 0.04)',
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTest(test);
                            }}
                            sx={{
                              color: '#666',
                              '&:hover': {
                                color: '#d32f2f',
                                bgcolor: 'rgba(211, 47, 47, 0.04)',
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Ranking View */}
          {!dataLoading && currentView === 'ranking' && (
            <Box ref={rankingSectionRef} sx={{ px: 2 }}>
              {rankingLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ pb: 2 }}>
                  {/* 독서 통계 요약 - Apple 스타일 개선 */}
                  <Box sx={{ mb: 5 }}>
                    
                                          <Box sx={{ 
                        mt: 4,
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(2, 1fr)', 
                        gap: 2.5
                      }}>
                        <Box sx={{ 
                          p: 3, 
                          bgcolor: '#ffffff', 
                          borderRadius: 3, 
                          border: '0.5px solid rgba(0,0,0,0.04)',
                          textAlign: 'center',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04)',
                          }
                        }}>
                          <Typography 
                            variant="h3" 
                            sx={{ 
                              fontWeight: 800, 
                              color: '#f59e0b', 
                              mb: 1,
                              fontSize: '2.25rem',
                              lineHeight: 1,
                              letterSpacing: '-0.02em'
                            }}
                          >
                            {rankingData.readingStats.averageRating ? rankingData.readingStats.averageRating.toFixed(1) : '0.0'}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#86868b', 
                              fontWeight: 500,
                              fontSize: '0.8125rem',
                              letterSpacing: '0.01em'
                            }}
                          >
                            평균 별점
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          p: 3, 
                          bgcolor: '#ffffff', 
                          borderRadius: 3, 
                          border: '0.5px solid rgba(0,0,0,0.04)',
                          textAlign: 'center',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04)',
                          }
                        }}>
                          <Typography 
                            variant="h3" 
                            sx={{ 
                              fontWeight: 800, 
                              color: '#10b981', 
                              mb: 1,
                              fontSize: '2.25rem',
                              lineHeight: 1,
                              letterSpacing: '-0.02em'
                            }}
                          >
                            {rankingData.readingStats.completedBooks || 0}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#86868b', 
                              fontWeight: 500,
                              fontSize: '0.8125rem',
                              letterSpacing: '0.01em'
                            }}
                          >
                            완독한 책
                          </Typography>
                        </Box>
                      </Box>
                  </Box>

                  {/* 별점 기준 TOP 책들 - Apple 스타일 개선 */}
                  {rankingData.topRatedBooks && rankingData.topRatedBooks.length > 0 && (
                    <Box sx={{ mb: 5 }}>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          mb: 3, 
                          fontWeight: 700, 
                          color: '#1d1d1f',
                          fontSize: '1.375rem',
                          letterSpacing: '-0.01em'
                        }}
                      >
                        ⭐ 별점 높은 책 TOP {Math.min(5, rankingData.topRatedBooks?.length || 0)}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {(rankingData.topRatedBooks || []).slice(0, 5).map((book, index) => (
                          <Box 
                            key={book.id}
                            onClick={() => navigate(`/book/${book.id}`)}
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              p: 2.5, 
                              bgcolor: '#ffffff', 
                              borderRadius: 3, 
                              border: '0.5px solid rgba(0,0,0,0.04)',
                              cursor: 'pointer',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
                              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 8px 25px rgba(26,35,126,0.12), 0 3px 10px rgba(26,35,126,0.08)',
                                borderColor: 'rgba(26,35,126,0.1)',
                              },
                              '&:active': {
                                transform: 'translateY(-1px)',
                              }
                            }}
                          >
                            <Box sx={{ 
                              width: 32, 
                              height: 32, 
                              borderRadius: '50%', 
                              bgcolor: index < 3 ? 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)' : '#f3f4f6',
                              color: index < 3 ? '#ffffff' : '#9ca3af',
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              mr: 2.5,
                              fontSize: '0.875rem',
                              fontWeight: 700,
                              boxShadow: index < 3 ? '0 2px 8px rgba(245,158,11,0.3)' : 'none',
                              background: index < 3 ? 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)' : '#f3f4f6'
                            }}>
                              {index + 1}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                  fontWeight: 600, 
                                  mb: 0.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  color: '#1d1d1f',
                                  fontSize: '1rem',
                                  lineHeight: 1.3,
                                  letterSpacing: '-0.01em'
                                }}
                              >
                                {book.title}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: '#86868b', 
                                  fontSize: '0.8125rem',
                                  fontWeight: 500
                                }}
                              >
                                {book.authors?.join(', ') || '작가 정보 없음'}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                px: 1.5,
                                py: 0.5,
                                bgcolor: '#fef3c7',
                                borderRadius: 2,
                                border: '0.5px solid rgba(245,158,11,0.2)'
                              }}>
                                <Typography sx={{ color: '#f59e0b', fontSize: '0.875rem', mr: 0.5 }}>★</Typography>
                                <Typography sx={{ 
                                  fontWeight: 700, 
                                  color: '#92400e',
                                  fontSize: '0.875rem'
                                }}>
                                  {book.starRating}
                                </Typography>
                              </Box>
                              {bookHighlightCounts[book.id] > 0 && (
                                <Box sx={{ 
                                  px: 1.5, 
                                  py: 0.5, 
                                  bgcolor: '#f3f4f6', 
                                  borderRadius: 2,
                                  fontSize: '0.75rem',
                                  color: '#6b7280',
                                  fontWeight: 500,
                                  border: '0.5px solid rgba(0,0,0,0.04)'
                                }}>
                                  {bookHighlightCounts[book.id]}개
                                </Box>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* 하이라이트 많은 책 TOP - Apple 스타일 개선 */}
                  {rankingData.mostHighlightedBooks && rankingData.mostHighlightedBooks.length > 0 && (
                    <Box sx={{ mb: 5 }}>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          mb: 3, 
                          fontWeight: 700, 
                          color: '#1d1d1f',
                          fontSize: '1.375rem',
                          letterSpacing: '-0.01em'
                        }}
                      >
                        💭 문장 많은 책 TOP {Math.min(5, rankingData.mostHighlightedBooks?.length || 0)}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {(rankingData.mostHighlightedBooks || []).slice(0, 5).map((book, index) => (
                          <Box 
                            key={book.id}
                            onClick={() => navigate(`/book/${book.id}`)}
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              p: 2.5, 
                              bgcolor: '#ffffff', 
                              borderRadius: 3, 
                              border: '0.5px solid rgba(0,0,0,0.04)',
                              cursor: 'pointer',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
                              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 8px 25px rgba(40,53,147,0.12), 0 3px 10px rgba(40,53,147,0.08)',
                                borderColor: 'rgba(40,53,147,0.1)',
                              },
                              '&:active': {
                                transform: 'translateY(-1px)',
                              }
                            }}
                          >
                            <Box sx={{ 
                              width: 32, 
                              height: 32, 
                              borderRadius: '50%', 
                              bgcolor: index < 3 ? 'linear-gradient(135deg, #283593 0%, #3f51b5 100%)' : '#f3f4f6',
                              color: index < 3 ? '#ffffff' : '#9ca3af',
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              mr: 2.5,
                              fontSize: '0.875rem',
                              fontWeight: 700,
                              boxShadow: index < 3 ? '0 2px 8px rgba(40,53,147,0.3)' : 'none',
                              background: index < 3 ? 'linear-gradient(135deg, #283593 0%, #3f51b5 100%)' : '#f3f4f6'
                            }}>
                              {index + 1}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                  fontWeight: 600, 
                                  mb: 0.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  color: '#1d1d1f',
                                  fontSize: '1rem',
                                  lineHeight: 1.3,
                                  letterSpacing: '-0.01em'
                                }}
                              >
                                {book.title}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: '#86868b', 
                                  fontSize: '0.8125rem',
                                  fontWeight: 500
                                }}
                              >
                                {book.authors?.join(', ') || '작가 정보 없음'}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box sx={{ 
                                px: 2, 
                                py: 0.75, 
                                bgcolor: '#e8eaf6', 
                                borderRadius: 2,
                                color: '#283593',
                                fontSize: '0.875rem',
                                fontWeight: 700,
                                border: '0.5px solid rgba(40,53,147,0.2)',
                                minWidth: '44px',
                                textAlign: 'center'
                              }}>
                                {bookHighlightCounts[book.id]}개
                              </Box>
                              {book.starRating > 0 && (
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center',
                                  px: 1.5,
                                  py: 0.5,
                                  bgcolor: '#fef3c7',
                                  borderRadius: 2,
                                  border: '0.5px solid rgba(245,158,11,0.2)'
                                }}>
                                  <Typography sx={{ color: '#f59e0b', fontSize: '0.75rem', mr: 0.5 }}>★</Typography>
                                  <Typography sx={{ 
                                    fontSize: '0.75rem', 
                                    color: '#92400e',
                                    fontWeight: 600
                                  }}>
                                    {book.starRating}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* 선호 태그 분석 - Apple 스타일 개선 */}
                  {(rankingData.favoriteGenres?.topEmotions?.length > 0 || 
                    rankingData.favoriteGenres?.topPurposes?.length > 0 || 
                    rankingData.favoriteGenres?.topOwnership?.length > 0) && (
                    <Box sx={{ mb: 5 }}>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          mb: 3, 
                          fontWeight: 700, 
                          color: '#1d1d1f',
                          fontSize: '1.375rem',
                          letterSpacing: '-0.01em'
                        }}
                      >
                        🏷️ 나의 독서 성향
                      </Typography>
                      
                      {/* 감정 태그 */}
                      {rankingData.favoriteGenres?.topEmotions?.length > 0 && (
                        <Box sx={{ mb: 4 }}>
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              mb: 2, 
                              fontWeight: 600, 
                              color: '#1d1d1f',
                              fontSize: '1.125rem',
                              letterSpacing: '-0.01em'
                            }}
                          >
                            자주 느끼는 감정
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            {(rankingData.favoriteGenres?.topEmotions || []).map(([emotion, count]) => {
                              const emotionMap = {
                                '몰입': { label: '🔥 몰입', color: '#1a237e', bgColor: '#e8eaf6' },
                                '통찰': { label: '💡 통찰', color: '#283593', bgColor: '#e1f5fe' },
                                '유쾌': { label: '😊 유쾌', color: '#f57c00', bgColor: '#fff3e0' },
                                '지루': { label: '😴 지루', color: '#6c757d', bgColor: '#f8f9fa' },
                                '답답': { label: '😤 답답', color: '#495057', bgColor: '#f1f3f4' }
                              };
                              const item = emotionMap[emotion] || { label: emotion, color: '#6c757d', bgColor: '#f8f9fa' };
                              return (
                                <Box 
                                  key={emotion}
                                  sx={{
                                    px: 2,
                                    py: 1,
                                    bgcolor: item.bgColor,
                                    color: item.color,
                                    borderRadius: 3,
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    border: `0.5px solid ${item.color}20`,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                  }}
                                >
                                  {item.label} 
                                  <span style={{ 
                                    fontSize: '0.75rem', 
                                    opacity: 0.7,
                                    fontWeight: 500,
                                    background: `${item.color}15`,
                                    padding: '2px 6px',
                                    borderRadius: '8px'
                                  }}>
                                    {count}
                                  </span>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}

                      {/* 읽은 이유 */}
                      {rankingData.favoriteGenres?.topPurposes?.length > 0 && (
                        <Box sx={{ mb: 4 }}>
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              mb: 2, 
                              fontWeight: 600, 
                              color: '#1d1d1f',
                              fontSize: '1.125rem',
                              letterSpacing: '-0.01em'
                            }}
                          >
                            주요 독서 목적
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            {(rankingData.favoriteGenres?.topPurposes || []).map(([purpose, count]) => {
                              const purposeMap = {
                                '성장': { label: '💪 성장', color: '#1a237e', bgColor: '#e8eaf6' },
                                '힐링': { label: '❤️ 힐링', color: '#e91e63', bgColor: '#fce4ec' },
                                '세계관확장': { label: '🌍 세계관 확장', color: '#283593', bgColor: '#e1f5fe' },
                                '견문넓히기': { label: '👁️ 견문 넓히기', color: '#00796b', bgColor: '#e0f2f1' },
                                '업무도움': { label: '💼 업무 도움', color: '#5d4037', bgColor: '#efebe9' },
                                '지식습득': { label: '🧠 지식 습득', color: '#1a237e', bgColor: '#e8eaf6' },
                                '재미오락': { label: '🎭 재미/오락', color: '#f57c00', bgColor: '#fff3e0' },
                                '호기심해결': { label: '🔍 호기심 해결', color: '#283593', bgColor: '#e1f5fe' }
                              };
                              const item = purposeMap[purpose] || { label: purpose, color: '#6c757d', bgColor: '#f8f9fa' };
                              return (
                                <Box 
                                  key={purpose}
                                  sx={{
                                    px: 2,
                                    py: 1,
                                    bgcolor: item.bgColor,
                                    color: item.color,
                                    borderRadius: 3,
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    border: `0.5px solid ${item.color}20`,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                  }}
                                >
                                  {item.label} 
                                  <span style={{ 
                                    fontSize: '0.75rem', 
                                    opacity: 0.7,
                                    fontWeight: 500,
                                    background: `${item.color}15`,
                                    padding: '2px 6px',
                                    borderRadius: '8px'
                                  }}>
                                    {count}
                                  </span>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}

                      {/* 소장 방식 */}
                      {rankingData.favoriteGenres?.topOwnership?.length > 0 && (
                        <Box>
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              mb: 2, 
                              fontWeight: 600, 
                              color: '#1d1d1f',
                              fontSize: '1.125rem',
                              letterSpacing: '-0.01em'
                            }}
                          >
                            주요 소장 방식
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            {(rankingData.favoriteGenres?.topOwnership || []).map(([ownership, count]) => {
                              const ownershipMap = {
                                '소장중': { label: '📦 소장 중', color: '#1a237e', bgColor: '#e8eaf6' },
                                '읽고판매함': { label: '💸 읽고 판매함', color: '#795548', bgColor: '#efebe9' },
                                '빌려읽음': { label: '📖 빌려 읽음', color: '#283593', bgColor: '#e1f5fe' },
                                '선물받음': { label: '🎁 선물받음', color: '#f57c00', bgColor: '#fff3e0' },
                                '구매예정': { label: '💰 구매 예정', color: '#388e3c', bgColor: '#e8f5e8' }
                              };
                              const item = ownershipMap[ownership] || { label: ownership, color: '#6c757d', bgColor: '#f8f9fa' };
                              return (
                                <Box 
                                  key={ownership}
                                  sx={{
                                    px: 2,
                                    py: 1,
                                    bgcolor: item.bgColor,
                                    color: item.color,
                                    borderRadius: 3,
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    border: `0.5px solid ${item.color}20`,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                  }}
                                >
                                  {item.label} 
                                  <span style={{ 
                                    fontSize: '0.75rem', 
                                    opacity: 0.7,
                                    fontWeight: 500,
                                    background: `${item.color}15`,
                                    padding: '2px 6px',
                                    borderRadius: '8px'
                                  }}>
                                    {count}
                                  </span>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* 빈 상태 */}
                  {(!rankingData.topRatedBooks || rankingData.topRatedBooks.length === 0) && 
                   (!rankingData.mostHighlightedBooks || rankingData.mostHighlightedBooks.length === 0) && (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 16px',
                        }}
                      >
                        <EmojiEventsIcon sx={{ fontSize: 32, color: '#bbb' }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#666' }}>
                        아직 랭킹 데이터가 없어요
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
                        책에 별점을 매기거나 문장을 추가해보세요
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Dialog
          open={isShareDialogOpen}
          onClose={() => setIsShareDialogOpen(false)}
          PaperProps={{ sx: { /* ... 공유 다이얼로그 스타일 ... */ } }}
        >
          <DialogTitle sx={{ /* ... 다이얼로그 제목 스타일 ... */ }}>
            공유하기
            <IconButton onClick={() => setIsShareDialogOpen(false)}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent>
            <List>
              <ListItem onClick={handleInstagramShare} sx={{ cursor: 'pointer' }}><ListItemIcon><InstagramIcon /></ListItemIcon><ListItemText primary="인스타그램 DM으로 공유" /></ListItem>
              <ListItem onClick={handleShareKakao} sx={{ cursor: 'pointer' }}><ListItemIcon><ChatIcon /></ListItemIcon><ListItemText primary="카카오톡으로 공유" /></ListItem>
              <ListItem onClick={handleCopyLink} sx={{ cursor: 'pointer' }}><ListItemIcon><ContentCopyIcon /></ListItemIcon><ListItemText primary="링크 복사하기" /></ListItem>
            </List>
          </DialogContent>
        </Dialog>

        {/* 테스트 제목 수정 다이얼로그 */}
        <Dialog
          open={isTestEditDialogOpen}
          onClose={() => setIsTestEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              maxWidth: '500px',
            }
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: 600, 
            fontFamily: 'NanumSquareB',
            pb: 1
          }}>
            제목 수정하기
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography sx={{ 
              color: 'text.secondary', 
              mb: 2,
              fontFamily: 'NanumSquareR',
              fontSize: '0.9rem'
            }}>
              원하는 이름을 지어주세요.
            </Typography>
            <TextField
              fullWidth
              value={editedTestTitle}
              onChange={handleTestTitleChange}
              error={!!testTitleError}
              helperText={testTitleError || `${editedTestTitle.length}/50자`}
              placeholder="테스트 제목을 입력하세요"
              variant="outlined"
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  fontFamily: 'NanumSquareR',
                }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button 
              onClick={() => setIsTestEditDialogOpen(false)}
              sx={{ 
                fontFamily: 'NanumSquareR',
                color: 'text.secondary'
              }}
            >
              취소
            </Button>
            <Button
              onClick={handleSaveTestTitle}
              variant="contained"
              disabled={!!testTitleError || !editedTestTitle.trim() || testActionLoading}
              sx={{
                bgcolor: '#1a237e',
                color: 'white',
                fontWeight: 600,
                borderRadius: '20px',
                px: 3,
                textTransform: 'none',
                fontFamily: 'NanumSquareB',
                '&:hover': {
                  bgcolor: '#283593',
                },
                '&:disabled': {
                  bgcolor: '#e0e0e0',
                  color: '#999',
                },
              }}
            >
              {testActionLoading ? <CircularProgress size={20} color="inherit" /> : '저장'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 테스트 삭제 확인 다이얼로그 */}
        <Dialog
          open={isTestDeleteDialogOpen}
          onClose={() => setIsTestDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              maxWidth: '500px',
            }
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: 600, 
            fontFamily: 'NanumSquareB',
            pb: 1
          }}>
            테스트 삭제
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography sx={{ 
              mb: 2,
              fontFamily: 'NanumSquareR',
              fontSize: '1rem'
            }}>
              <strong>{selectedTest?.title || '이 테스트'}</strong>를 삭제하시겠습니까?
            </Typography>
            <Typography sx={{ 
              color: 'text.secondary',
              fontFamily: 'NanumSquareR',
              fontSize: '0.9rem'
            }}>
              삭제된 테스트는 복구할 수 없습니다.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button 
              onClick={() => setIsTestDeleteDialogOpen(false)}
              sx={{ 
                fontFamily: 'NanumSquareR',
                color: 'text.secondary'
              }}
            >
              취소
            </Button>
            <Button
              onClick={handleConfirmDeleteTest}
              variant="contained"
              disabled={testActionLoading}
              sx={{
                bgcolor: '#d32f2f',
                color: 'white',
                fontWeight: 600,
                borderRadius: '20px',
                px: 3,
                textTransform: 'none',
                fontFamily: 'NanumSquareB',
                '&:hover': {
                  bgcolor: '#c62828',
                },
                '&:disabled': {
                  bgcolor: '#e0e0e0',
                  color: '#999',
                },
              }}
            >
              {testActionLoading ? <CircularProgress size={20} color="inherit" /> : '삭제'}
            </Button>
          </DialogActions>
        </Dialog>
  
        <Menu
          id="highlight-menu" 
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => { setAnchorEl(null); setSelectedHighlight(null); }}
        >
          <MenuItem onClick={handleEditHighlightMenu}>
            <EditIcon sx={{ mr: 1 }} /> 수정
          </MenuItem>
          <MenuItem onClick={handleDeleteHighlight}> 
            <DeleteIcon sx={{ mr: 1 }} /> 삭제
          </MenuItem>
        </Menu>
  
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth>
          <DialogTitle>문장 수정</DialogTitle>
          <DialogContent>
            <TextField value={editedText} onChange={(e) => setEditedText(e.target.value)} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>취소</Button>
            <Button 
              onClick={handleEditHighlight} 
              variant="contained" 
              sx={{ 
                bgcolor: 'grey.900', 
                color: 'white', 
                '&:hover': { bgcolor: 'grey.800' } 
              }}
            >
              저장
            </Button>
          </DialogActions>
        </Dialog>
  
        <Dialog 
          open={isNicknameDialogOpen} 
          onClose={handleCancelEdit}
          PaperProps={{ sx: { /* ... 닉네임 다이얼로그 스타일 ... */ } }}
        >
          <DialogTitle>닉네임 수정</DialogTitle>
          <DialogContent>
            <TextField value={newNickname} onChange={handleNicknameChange} error={!!nicknameError} helperText={nicknameError || "..."} />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelEdit}>취소</Button>
            <Button 
              onClick={handleProfileEdit} 
              variant="contained"
              disabled={!!nicknameError || newNickname === profileUser?.displayName || !newNickname} 
              sx={{ 
                bgcolor: 'grey.900',
                color: 'white',
                '&:hover': { bgcolor: 'grey.800' },
                '&.Mui-disabled': { bgcolor: 'grey.500' }
              }}
            >
              저장
            </Button>
          </DialogActions>
        </Dialog>
  
        <Dialog
          open={isFollowingListOpen}
          onClose={handleCloseFollowingList}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              position: 'fixed',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              m: 0,
              width: '100%',
              maxWidth: '600px',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '70vh',
            }
          }}
          aria-labelledby="following-list-dialog-title"
        >
          <DialogTitle id="following-list-dialog-title" sx={{ textAlign: 'center', pb: 1, pt: 2 }}>
            SnapList
            <IconButton
              aria-label="close"
              onClick={handleCloseFollowingList}
              sx={{
                position: 'absolute',
                right: 12,
                top: 12,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          {/* SnapList 안내문구 */}
          <Box sx={{ textAlign: 'center', px: 3, pb: 1, color: 'text.secondary', fontSize: '1.03rem', fontFamily: 'Noto Sans, Pretendard, sans-serif' }}>
            영감을 주는 사람을 추가해보세요.<br/>
            상대방은 당신이 추가한 사실을 알 수 없습니다.
          </Box>
          <DialogContent sx={{ p: 0, overflowY: 'auto' }}>
            {isFollowingListLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
                <CircularProgress />
              </Box>
            ) : followingList.length === 0 ? (
              <Typography sx={{ textAlign: 'center', p: 4, color: 'text.secondary' }}>
                SnapList에 추가한 사용자가 없습니다.
              </Typography>
            ) : (
              <List disablePadding>
                {followingList.map((user) => (
                  <ListItem
                    key={user.id}
                    button
                    component={Link}
                    to={`/u/${user.id}`}
                    onClick={handleCloseFollowingList}
                    sx={{ px: 2.5, py: 1.5, color: 'inherit', alignItems: user.bio ? 'flex-start' : 'center', minHeight: 64 }}
                  >
                    <ListItemText
                      primary={user.displayName}
                      secondary={
                        user.bio
                          ? (
                              <span style={{
                                display: 'block',
                                maxWidth: 220,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                color: '#888',
                                fontSize: '0.93rem',
                                fontStyle: 'normal',
                              }}>{user.bio}</span>
                            )
                          : null
                      }
                      primaryTypographyProps={{ sx: { fontWeight: 500, fontSize: '0.95rem' } }}
                      secondaryTypographyProps={{ sx: { fontSize: '0.8rem', color: 'text.secondary' } }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
        </Dialog>
  
        <Dialog
          open={isFollowerDialogOpen}
          onClose={() => setIsFollowerDialogOpen(false)}
          fullWidth
          maxWidth="xs"
          PaperProps={{
            sx: {
              position: 'fixed',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              m: 0,
              width: '100%',
              maxWidth: '600px',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              textAlign: 'center',
              p: 3,
              boxShadow: 8,
              borderRadius: '16px',
              bgcolor: '#fff',
              overflow: 'visible',
            }
          }}
        >
          <DialogTitle sx={{ pt: 3, pb: 1, fontWeight: 600, fontSize: '1.15rem', textAlign: 'center' }}>Readers</DialogTitle>
          <DialogContent>
            <Typography sx={{ color: 'text.secondary', fontSize: '1.05rem', py: 2, textAlign: 'center', fontFamily: 'Noto Sans, Pretendard, sans-serif' }}>
              Readers 목록은 공개되지 않아요.<br/>
              당신의 기록을 조용히 좋아하는 사람들의 수만 보여드립니다.
            </Typography>
          </DialogContent>
          <Button onClick={() => setIsFollowerDialogOpen(false)} fullWidth sx={{ color: 'text.secondary', mt: 1, fontWeight: 500, fontSize: '1.05rem', borderRadius: 0, minHeight: 48 }}>닫기</Button>
        </Dialog>
  
        <InAppBrowserModal 
          open={inAppBrowserModalOpen} 
          onClose={() => setInAppBrowserModalOpen(false)} 
        />

        <Dialog
          open={moreSheetOpen}
          onClose={() => setMoreSheetOpen(false)}
          fullWidth
          PaperProps={{ sx: bottomSheetPaperSx }}
          TransitionComponent={Slide}
          TransitionProps={{ direction: 'up' }}
          keepMounted
        >
          <Box sx={{ pt: 2, pb: 1, textAlign: 'center', fontWeight: 600, fontSize: '1.1rem' }}>더보기</Box>
          <Box sx={{ p: 0 }}>
            <Button
              fullWidth
              onClick={() => { handleShareKakao(); setMoreSheetOpen(false); }}
              startIcon={<ChatIcon />}
                sx={sheetButtonSx}
            >
              친구에게 카톡으로 공유하기
            </Button>
            <Button
              fullWidth
              onClick={() => { handleCopyLink(); setMoreSheetOpen(false); }}
              startIcon={<ContentCopyIcon />}
              sx={sheetButtonSx}
            >
              프로필 링크 복사하기
            </Button>
            {isOwner && (
              <Button
                fullWidth
                onClick={() => { navigate('/mypage/nickname-edit'); setMoreSheetOpen(false); }}
                startIcon={<EditIcon />}
                sx={sheetButtonSx}
              >
                닉네임 수정
              </Button>
            )}
            {isOwner && (
              <Button
                fullWidth
                onClick={() => { navigate('/mypage/bio-edit'); setMoreSheetOpen(false); }}
                startIcon={<EditIcon />}
                sx={sheetButtonSx}
              >
                소개메세지 수정
              </Button>
            )}
            {isOwner && (
              <Button
                fullWidth
                onClick={() => { handleLogout(); setMoreSheetOpen(false); }}
                startIcon={<LogoutIcon />}
                sx={sheetButtonDangerSx}
              >
                로그아웃
              </Button>
            )}
          </Box>
          <Button onClick={() => setMoreSheetOpen(false)} fullWidth sx={{ color: 'text.secondary', mt: 1, fontWeight: 500, fontSize: '1.05rem', borderRadius: 0, minHeight: 48 }}>닫기</Button>
        </Dialog>

        <SnapFollowBottomSheet
          open={showSnapFollowSheet}
          onClose={() => setShowSnapFollowSheet(false)}
          targetName={profileUser?.displayName || '사용자'}
          onFollow={handleFollowToggle}
          isFollowing={isFollowing}
          isLoggedIn={!!currentUser}
        />

        {/* 스낵바 - 링크 복사 완료 메시지 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          TransitionComponent={Slide}
          TransitionProps={{ direction: 'up' }}
          sx={{
            '& .MuiSnackbar-root': {
              bottom: { xs: 16, sm: 24 },
            },
            maxWidth: { xs: 'calc(100vw - 32px)', sm: '400px' },
            width: '100%',
          }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ 
              width: '100%',
              borderRadius: '16px',
              fontFamily: 'NanumSquareR',
              fontSize: '0.95rem',
              fontWeight: 600,
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              border: '1px solid',
              borderColor: snackbar.severity === 'success' ? 'rgba(26, 35, 126, 0.2)' : 
                          snackbar.severity === 'error' ? 'rgba(211, 47, 47, 0.2)' : 
                          snackbar.severity === 'warning' ? 'rgba(255, 193, 7, 0.2)' : 
                          'rgba(26, 35, 126, 0.2)',
              display: 'flex',
              alignItems: 'center',
              minHeight: '56px',
              padding: '12px 16px',
              '& .MuiAlert-icon': {
                fontSize: '1.3rem',
                marginRight: '12px',
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
              },
              '& .MuiAlert-message': {
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                lineHeight: 1.4,
              },
              '& .MuiAlert-action': {
                display: 'flex',
                alignItems: 'center',
                marginLeft: '8px',
                padding: 0,
              },
              '& .MuiAlert-action .MuiIconButton-root': {
                padding: '4px',
                margin: 0,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              },
              // 성공 메시지 특별 스타일 - 서비스 톤앤매너에 맞는 색상
              ...(snackbar.severity === 'success' && {
                background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
                color: 'white',
                '& .MuiAlert-icon': {
                  color: '#FFD700',
                },
                '& .MuiAlert-action .MuiIconButton-root': {
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                },
              }),
              // 에러 메시지 스타일
              ...(snackbar.severity === 'error' && {
                background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
                color: 'white',
                '& .MuiAlert-icon': {
                  color: 'white',
                },
                '& .MuiAlert-action .MuiIconButton-root': {
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                },
              }),
              // 경고 메시지 스타일
              ...(snackbar.severity === 'warning' && {
                background: 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)',
                color: 'white',
                '& .MuiAlert-icon': {
                  color: 'white',
                },
                '& .MuiAlert-action .MuiIconButton-root': {
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                },
              }),
              // 정보 메시지 스타일
              ...(snackbar.severity === 'info' && {
                background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
                color: 'white',
                '& .MuiAlert-icon': {
                  color: 'white',
                },
                '& .MuiAlert-action .MuiIconButton-root': {
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                },
              }),
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
  );
}

export default MyPage;